// pieces and components
const initBadgeTooltips = () => {
	const badgeTooltipSelector = ".js-badge-tooltip";
	const clippingOverflowValues = new Set(["auto", "clip", "hidden", "scroll"]);
	const horizontalGutter = 4;

	const getHorizontalClippingBounds = (tooltipContent) => {
		let leftBoundary = horizontalGutter;
		let rightBoundary = window.innerWidth - horizontalGutter;
		let ancestorElement = tooltipContent.parentElement;

		while (ancestorElement && ancestorElement !== document.documentElement) {
			const ancestorStyles = window.getComputedStyle(ancestorElement);

			if (clippingOverflowValues.has(ancestorStyles.overflowX)) {
				const ancestorBounds = ancestorElement.getBoundingClientRect();
				leftBoundary = Math.max(
					leftBoundary,
					ancestorBounds.left + horizontalGutter
				);
				rightBoundary = Math.min(
					rightBoundary,
					ancestorBounds.right - horizontalGutter
				);
			}

			ancestorElement = ancestorElement.parentElement;
		}

		return { leftBoundary, rightBoundary };
	};

	const positionBadgeTooltip = (badgePill) => {
		const tooltipContent = badgePill.querySelector(".btn__tooltip__content");
		if (!tooltipContent) return;

		tooltipContent.style.setProperty("--tooltip-offset-x", "0px");
		tooltipContent.style.removeProperty("--tooltip-available-width");

		const { leftBoundary, rightBoundary } =
			getHorizontalClippingBounds(tooltipContent);
		const availableWidth = Math.max(rightBoundary - leftBoundary, 1);
		tooltipContent.style.setProperty(
			"--tooltip-available-width",
			`${availableWidth}px`
		);

		const tooltipBounds = tooltipContent.getBoundingClientRect();
		let horizontalOffset = 0;

		if (tooltipBounds.left < leftBoundary) {
			horizontalOffset = leftBoundary - tooltipBounds.left;
		}

		if (tooltipBounds.right + horizontalOffset > rightBoundary) {
			horizontalOffset +=
				rightBoundary - (tooltipBounds.right + horizontalOffset);
		}

		tooltipContent.style.setProperty(
			"--tooltip-offset-x",
			`${horizontalOffset}px`
		);
	};

	document.addEventListener("pointerover", (event) => {
		if (!(event.target instanceof Element)) return;

		const badgePill = event.target.closest(badgeTooltipSelector);
		if (!badgePill) return;
		if (
			event.relatedTarget instanceof Node &&
			badgePill.contains(event.relatedTarget)
		)
			return;

		positionBadgeTooltip(badgePill);
	});

	document.addEventListener("focusin", (event) => {
		if (!(event.target instanceof Element)) return;

		const badgePill = event.target.closest(badgeTooltipSelector);
		if (badgePill) positionBadgeTooltip(badgePill);
	});

	window.addEventListener(
		"resize",
		() => {
			document
				.querySelectorAll(
					`${badgeTooltipSelector}:hover, ${badgeTooltipSelector}:focus-within`
				)
				.forEach(positionBadgeTooltip);
		},
		{ passive: true }
	);
};

const initProductBis = () => {
	// Fills the dialog in before showing it, so it opens via openDialog() rather
	// than the generic .js-dialog-open delegate.
	on("body", "click", ".js-bis-trigger", (e) => {
		const target = e.target.closest(".js-bis-trigger");
		// Check for truly PDP BIS by excluding c-item-card on PDP pages
		const isBisItemCard = target.closest(".c-item-card.js-bis-product");
		const isPDP = root.id == "template-product-single" && !isBisItemCard;
		// The quick-add panel renders the PDP form off-PDP, so reuse the PDP flow:
		// read the live input[name="id"] and only fire BIS for sold-out variants.
		const isQuickAddPanel = target.closest(".js-quick-add-panel");
		const usePdpFlow = isPDP || isQuickAddPanel;

		// Inside the quick-add panel, only the CTA opens BIS. Clicking a sold-out
		// size just selects it, which flips the CTA to "Notify when available".
		if (isQuickAddPanel && !target.classList.contains("form-bis-trigger"))
			return false;

		if (
			usePdpFlow &&
			(!target.classList.contains("is-disabled") ||
				!target.classList.contains("is-bis-enabled"))
		)
			return false;

		const runHandler = () => {
			const bisProduct = target.closest(".js-bis-product");
			// The PDP form's trigger carries no data-dialog, so fall back to the
			// single back-in-stock dialog mounted in the layout.
			const dialogKey =
				target.closest("[data-dialog]")?.dataset.dialog || "product-bis";
			const targetPopup = document.querySelector(
				`dialog.c-dialog[data-dialog="${dialogKey}"]`
			);
			const targetBISForm = targetPopup?.querySelector(".js-product-bis");

			if (!bisProduct || !targetPopup || !targetBISForm) return;
			//refresh BIS popup - clear success class on c-email-back-in-stock form
			targetBISForm.classList.remove(
				"is-show-quick-link",
				"is-promote-steady-state",
				"is-success"
			);

			const idInput = bisProduct.querySelector('[name="id"]');
			const productId = usePdpFlow
				? idInput?.dataset.productId
				: target.dataset.productId;
			const variantId = usePdpFlow ? idInput?.value : target.dataset.variantId;
			const variantsJson = validateJson(
				(
					bisProduct.dataset.variantsJson ||
					bisProduct.querySelector("[data-variants-json]")?.dataset.variantsJson
				)
					.replace(/'/g, '"')
					.trim()
			);
			const variantObject = variantsJson.find(
				(variant) => variant.id === variantId
			);

			if (!variantObject) return;

			//retrieve all BIS content
			const {
				bisImage: productImage,
				bisMaterial: productMaterial,
				bisTitle: productTitle,
				bisDescription: productDescription,
				bisRelatedUrl: productRelatedUrl,
				promoteSteadyState: productPromoteSteadyState,
			} = bisProduct.dataset;

			//get size that belongs to the current color
			const { colorName: productColorName, sizeName: selectedSizeName } =
				variantObject;

			const showAllVariantsInDropDown =
				target.classList.contains("is-coming-soon");
			const variantSizes = variantsJson.filter(
				(v) =>
					v.colorName === productColorName &&
					(showAllVariantsInDropDown || v.inventory <= 0)
			);

			// DOM targets
			const bisProductImage = targetPopup.querySelector(".js-bis-image");
			const bisVariantPills = targetPopup.querySelector(".js-bis-pills");
			const bisProductTitle = targetPopup.querySelector(
				".js-bis-product-title"
			);
			const bisProductDescription = targetPopup.querySelector(
				".js-bis-description"
			);
			const bisQuickLink = targetPopup.querySelector(".js-bis-quick-link");
			const bisSwatch = targetPopup.querySelector(".js-bis-swatch");
			const bisSelector = targetPopup.querySelector(".js-bis-selector");

			const bisProductId = targetPopup.querySelector('[name="bis-product-id"]');
			const bisVariantId = targetPopup.querySelector('[name="bis-variant-id"]');

			//build the BIS popup
			targetBISForm.classList.toggle(
				"is-promote-steady-state",
				productPromoteSteadyState == "true"
			);

			if (productImage) {
				bisProductImage.innerHTML = `<img src="${productImage}" alt="${productTitle}" loading="lazy" />`;
			}

			bisVariantPills.innerHTML = `${
				productMaterial
					? `<li class="btn btn-pill">${productMaterial}</li>`
					: ""
			}`;

			bisProductTitle.textContent = productTitle || "";
			bisProductDescription.textContent = productDescription || "";

			if (productRelatedUrl.includes("#product-related")) {
				targetBISForm.classList.add("is-show-quick-link");
				bisQuickLink.href = productRelatedUrl;

				if (!isPDP) {
					Object.assign(bisQuickLink, {
						target: "_blank",
						rel: "noopener noreferrer",
					});
				}
			} else {
				// data-bis-related-url="false"
			}

			if (productColorName) {
				bisSwatch.innerHTML = `
					<span data-swatch-color="${productColorName}"></span>
					<span class="leading-none">${unslugify(productColorName)}</span>
				`;
			}

			if (variantSizes.length > 1) {
				const options = variantSizes
					.map(
						({ id, sizeName }) => `
							<li class="c-bis-popup__selector__option">
								<input id="${id}" class="js-bis-selector-option" type="radio" value="${id}" data-size-value="${sizeName}" ${
									sizeName === selectedSizeName ? "checked" : ""
								} />
								<label for="${id}">${sizeName}</label>
								<svg class="svg-arrow-right" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 37 36">
									<path fill="currentColor" d="M26.79,9.21A35.06,35.06,0,0,0,37,16.2v3.66a35.34,35.34,0,0,0-10.21,7A12.84,12.84,0,0,0,23.21,36H17.56q0-6.22,3-10a15.4,15.4,0,0,1,8.06-5.2V20.6H0V15.43H28.63v-.3A15.16,15.16,0,0,1,20.57,10q-3-3.77-3-10h5.65a13,13,0,0,0,.87,5A12.79,12.79,0,0,0,26.79,9.21Z"/>
								</svg>
							</li>
						`
					)
					.join("");

				bisSelector.innerHTML = `
					<button type="button" class="c-bis-popup__selector__trigger f-h f-a-c t-l-2 js-selector-trigger" aria-label="Toggle size selector">
						<div class="selector-display js-selector-display">${selectedSizeName}</div>
						<svg class="svg-caret-down" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 9 5">
							<path fill="none" stroke="currentColor" stroke-linejoin="round" stroke-linecap="round" d="M.5.5l4,4,4-4"/>
						</svg>
					</button>
					<ul class="c-bis-popup__selector__options">${options}</ul>
				`;
			} else if (variantSizes.length === 1) {
				bisSelector.innerHTML = `
				<div class="c-bis-popup__selector__trigger f-h f-a-c t-l-2">
					<div class="selector-display">${variantSizes[0].sizeName}</div>
				</div>
			`;
			}
			// Hidden fields
			bisProductId.value = productId || "";
			bisVariantId.value = variantId || "";

			openDialog(targetPopup, { trigger: target });
		};

		isPDP ? setTimeout(runHandler, 500) : runHandler();
	});

	on("body", "change", ".js-bis-selector-option", (e) => {
		const target = e.target.closest(".js-bis-selector-option");
		updateBisDropdown(target);
	});

	const updateBisDropdown = (target) => {
		const dropDown = target.closest(".js-bis-selector");
		const dropDownOptions = dropDown.querySelectorAll(
			".js-bis-selector-option"
		);

		dropDownOptions.forEach((option) => {
			option.checked = false;
		});
		target.checked = true;
		dropDown.querySelector(".js-selector-display").textContent =
			target.dataset.sizeValue;
		dropDown.classList.remove("is-active");

		//update bis variant id
		const bisVariantId = dropDown
			.closest(".c-bis-popup")
			.querySelector('[name="bis-variant-id"]');
		bisVariantId.value = target.value ? target.value : "";
	};

	//outside click
	const dropdowns = document.querySelectorAll(".js-bis-selector");
	dropdowns?.forEach((dropdown) => {
		document.addEventListener("click", (e) => {
			if (
				dropdown.classList.contains("is-active") &&
				e.target &&
				!e.target.classList.contains("js-selector-trigger") &&
				!e.target.closest(".js-selector-trigger") &&
				!e.target.classList.contains("js-selector-display") &&
				!e.target.closest(".js-selector-display")
			) {
				dropdown.classList.remove("is-active");
			}
		});
	});
};

// Product cards carry images that are in the layout but not on screen: the hover
// thumbnail sits at opacity 0 behind the default one. opacity 0 is still
// "visible" to lazysizes, so each card used to download two or more images — on
// touch devices as well, where the hover never happens. Those images render with
// their data-src/data-srcset intact but without the lazyload class, and are
// handed to lazysizes the first time a pointer or the keyboard reaches the card.
// The hover slider's slides are deferred the same way, but ComponentSlider
// unveils those itself, a slide at a time.
const unveilDeferredImage = (image) => {
	image.classList.remove("js-lazy-deferred");
	if (window.lazySizes) {
		window.lazySizes.loader.unveil(image);
	} else {
		image.classList.add("lazyload");
	}
};

const initDeferredCardImages = () => {
	const unveilCardImages = (event) => {
		const card = event.target.closest?.(".c-item-card");
		if (!card || card.dataset.imagesUnveiled) return;
		card.dataset.imagesUnveiled = "true";

		card.querySelectorAll(".js-lazy-deferred").forEach((image) => {
			if (image.closest("c-slider[data-embla-lazy]")) return;
			unveilDeferredImage(image);
		});
	};

	// Delegated from the document so cards appended by Load More, or swapped in by
	// a filter, need no rebinding.
	document.addEventListener("pointerover", unveilCardImages);
	document.addEventListener("focusin", unveilCardImages);
};

const initQuickAddPanel = () => {
	const panel = document.querySelector(".js-quick-add-panel");
	if (!panel) return;

	const content = panel.querySelector(".js-quick-add-content");

	// Cached per product URL so reopening is instant (see createSectionFetcher in
	// assets/js-initialize.js; it also drops its cache on a membership change).
	const quickAddSections = createSectionFetcher("quick-add-panel");

	const openPanel = (triggerButton) =>
		openDialog(panel, { trigger: triggerButton });
	const closePanel = () => closeDialog(panel);

	// Product URL currently shown, so a membership change can refresh the panel.
	let currentProductUrl = null;

	// Wait for the exit animation to finish before wiping the markup, so the
	// content doesn't visibly vanish mid-slide. Reopening cancels the close, in
	// which case `close` never fires and the content stays put.
	panel.addEventListener("close", () => {
		content.innerHTML = "";
		currentProductUrl = null;
	});

	const loadPanel = async (productUrl, isInitialOpen) => {
		currentProductUrl = productUrl;
		// An initial open is awaited before the sheet is shown, so it needs no
		// placeholder state. A re-fetch (colour change) stays visible and dims.
		if (!isInitialOpen) panel.classList.add("is-swapping");

		try {
			content.innerHTML = await quickAddSections.get(productUrl);

			// cProductForm.init() only scans forms present at page load, so the
			// injected form needs its sold-out combinations disabled manually.
			const form = content.querySelector(".c-product-form");
			if (form?.dataset.variantsJson) {
				const variantsJson = validateJson(
					form.dataset.variantsJson.replace(/'/g, '"').trim()
				);
				if (variantsJson) {
					cProductForm.updateVariantDisabledState(form, variantsJson);
				}
			}

			content
				.querySelectorAll(".c-video:not(.disable-autoplay) video")
				.forEach((video) => {
					loadLazyVideo(video);
					video.play().catch(() => {});
				});
		} catch (error) {
			console.error("Quick add panel failed to load:", error);
		} finally {
			panel.classList.remove("is-swapping");
		}
	};

	// Prefetch on press intent (pointerdown fires before click, for mouse + touch),
	// so the section is usually ready by the time the panel opens.
	on("body", "pointerdown", ".js-quick-add-trigger", (e) => {
		const trigger = e.target.closest(".js-quick-add-trigger");
		if (trigger?.dataset.productUrl) {
			quickAddSections.get(trigger.dataset.productUrl).catch(() => {});
		}
	});

	on("body", "click", ".js-quick-add-trigger", async (e) => {
		const trigger = e.target.closest(".js-quick-add-trigger");
		const productUrl = trigger.dataset.productUrl;
		if (!productUrl) return;

		await loadPanel(productUrl, true);
		openPanel(trigger);
	});

	// Combined-listing color swatches carry data-swatch-url and would navigate to
	// the PDP. Inside the panel, re-fetch the panel for that color's product instead.
	on("body", "click", ".js-variant-selector", (e) => {
		const target = e.target.closest(".js-variant-selector");
		if (!target.dataset.swatchUrl) return;
		if (!target.closest(".js-quick-add-panel")) return;

		e.preventDefault();
		loadPanel(target.dataset.swatchUrl, false);
	});

	// Membership changed: the fetcher has already dropped its cache (its listener
	// was registered first), so refresh the panel if open.
	document.addEventListener("bandit:membership-changed", () => {
		if (panel.open && currentProductUrl) {
			loadPanel(currentProductUrl, false);
		}
	});

	// Adding to cart opens the minicart over this sheet. Both are dialogs now, so
	// step aside for any dialog that opens above us rather than watching for the
	// cart's old state class.
	document.addEventListener("bandit:dialog-open", (event) => {
		if (event.target !== panel && panel.open) closePanel();
	});
};

// Product-card colour swatches swap the card to that colour in place instead of
// opening its PDP. The replacement is sections/s-item-card.liquid rendered for
// that colour's variant, so image, title, price, badges and the quick-add sizes
// all belong to it. The swatch href stays the clean canonical URL, for crawlers
// and for modifier clicks, which keep the browser default.
const initCardColorSwap = () => {
	const cardSections = createSectionFetcher("s-item-card", 20);

	const getVariantUrl = (swatch) => {
		const variantUrl = new URL(swatch.href, window.location.origin);
		variantUrl.searchParams.set("variant", swatch.dataset.variantId);
		return variantUrl;
	};

	// On a collection page, fetch through /collections/{handle}/products/… so the
	// card's colour swatches keep honouring that collection's exclude_from list.
	const getCardUrl = (swatch) => {
		const variantUrl = getVariantUrl(swatch);
		const collectionPath = window.location.pathname.match(
			/^(\/[a-z]{2}(-[a-z]{2})?)?\/collections\/[^/]+/i
		)?.[0];
		const productIndex = variantUrl.pathname.indexOf("/products/");
		const path =
			collectionPath && productIndex !== -1
				? collectionPath + variantUrl.pathname.slice(productIndex)
				: variantUrl.pathname;
		return path + variantUrl.search;
	};

	// Shopify redirects a collection-scoped product URL when the product isn't in
	// that collection (and always for /collections/all), so fall back to the bare
	// product URL; the card then only loses the exclude_from check.
	const fetchCardHtml = (cardUrl) =>
		cardSections.get(cardUrl).catch((error) => {
			const productUrl = cardUrl.replace(
				/^(\/[a-z]{2}(-[a-z]{2})?)?\/collections\/[^/]+/i,
				"$1"
			);
			if (productUrl === cardUrl) throw error;
			return cardSections.get(productUrl);
		});

	// Caller-only render params never reach the section, so copy them across.
	const carryCallerParams = (newCard, oldCard) => {
		const callerClass = oldCard.dataset.cardClass || "";
		newCard.dataset.cardClass = callerClass;
		newCard.classList.add(...callerClass.split(" ").filter(Boolean));

		if (oldCard.style.order) newCard.style.order = oldCard.style.order;

		if (oldCard.querySelector(".c-item-price-member-tooltip.is-top")) {
			newCard
				.querySelectorAll(".c-item-price-member-tooltip")
				.forEach((tooltip) => tooltip.classList.add("is-top"));
		}
	};

	// The new card's images are lazysizes placeholders, which would leave the
	// card blank until they load. Promote the visible one at the slot width the
	// old image was already using, and give it a moment to decode first.
	const preloadCardImage = async (newCard, oldCard) => {
		// The first image in the thumbnail is the visible one in every card layout
		// (default thumbnail, the slider's first slide, or the only image). Scoped
		// to the thumbnail so award badges, which come earlier, are never picked.
		const image = newCard.querySelector(".c-item-card__thumbnail img.lazyload");
		if (!image?.dataset.srcset) return;

		const oldImage = oldCard.querySelector(".c-item-card__thumbnail img");
		const slotWidth = oldImage?.getBoundingClientRect().width;
		promoteLazyImage(
			image,
			oldImage?.getAttribute("sizes") ||
				(slotWidth ? `${Math.round(slotWidth)}px` : "50vw")
		);

		const decodeTimeout = new Promise((resolve) => setTimeout(resolve, 1000));
		await Promise.race([image.decode(), decodeTimeout]).catch(() => {});
	};

	// A later click on the same card supersedes this one.
	const isSuperseded = (card, cardUrl) =>
		!card.isConnected ||
		(card.dataset.swapPending && card.dataset.swapPending !== cardUrl);

	const replaceCard = async (card, cardUrl, focusSwatchColor) => {
		const html = await fetchCardHtml(cardUrl);
		if (isSuperseded(card, cardUrl)) return;

		const newCard = parseHtmlString(html).querySelector(".c-item-card");
		if (!newCard) throw new Error(`No product card in ${cardUrl}`);
		// A parsed document never loads images; adopted, the promoted one below
		// downloads and can be decoded before the card goes in.
		document.adoptNode(newCard);

		carryCallerParams(newCard, card);
		newCard.dataset.cardSwapped = cardUrl;
		await preloadCardImage(newCard, card);
		if (isSuperseded(card, cardUrl)) return;

		// <c-slider> has no disconnectedCallback, so its Embla instance would
		// outlive the card.
		card.querySelectorAll("c-slider").forEach((slider) => {
			slider.embla?.destroy();
		});

		const swapCards = () => card.replaceWith(newCard);
		if (document.startViewTransition) {
			await document.startViewTransition(swapCards).updateCallbackDone;
		} else {
			swapCards();
		}

		if (focusSwatchColor) {
			Array.from(newCard.querySelectorAll(".js-colorway-swatch"))
				.find(
					(swatch) =>
						swatch.dataset.swatchColor === focusSwatchColor &&
						swatch.getClientRects().length
				)
				?.focus({ preventScroll: true });
		}
	};

	on("body", "pointerdown", ".js-colorway-swatch", (e, swatch) => {
		if (!swatch.dataset.variantId) return;
		fetchCardHtml(getCardUrl(swatch)).catch(() => {});
	});

	on("body", "click", ".js-colorway-swatch", async (e, swatch) => {
		if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
		const card = swatch.closest(".c-item-card");
		if (!card || !swatch.dataset.variantId) return;

		e.preventDefault();
		if (swatch.getAttribute("aria-current") === "true") return;

		const cardUrl = getCardUrl(swatch);
		if (card.dataset.swapPending === cardUrl) return;
		card.dataset.swapPending = cardUrl;
		card.setAttribute("aria-busy", "true");

		try {
			await replaceCard(
				card,
				cardUrl,
				card.contains(document.activeElement) && swatch.dataset.swatchColor
			);
		} catch (error) {
			console.error("Card colour swap failed, opening the PDP instead:", error);
			window.location.assign(getVariantUrl(swatch).href);
		} finally {
			if (card.dataset.swapPending === cardUrl) {
				delete card.dataset.swapPending;
				card.removeAttribute("aria-busy");
			}
		}
	});

	// Members-only gating is rendered server-side. updateProductCards refreshes
	// cards by matching them to this page's markup, which a swapped card may not
	// be in, so swapped cards refresh themselves. The fetcher has already
	// dropped its cache (its listener was registered first).
	document.addEventListener("bandit:membership-changed", () => {
		document.querySelectorAll("[data-card-swapped]").forEach((card) => {
			replaceCard(card, card.dataset.cardSwapped).catch((error) => {
				console.error("Card refresh failed:", error);
			});
		});
	});
};

/**
 * MouseFollower
 * Makes a target element follow the mouse within a parent container
 * Usage: Add .js-mouse-follow-parent to container, .js-mouse-follow-target to follower
 */
class MouseFollower {
	constructor(container, options = {}) {
		this.container =
			typeof container === "string"
				? document.querySelector(container)
				: container;

		if (!this.container) {
			console.warn("MouseFollower: Container element not found");
			return;
		}

		// Skip initialization on touch devices
		if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
			return;
		}

		this.options = {
			targetSelector: ".js-mouse-follow-target",
			cursorOffsetX: 0,
			cursorOffsetY: 0,
			duration: 0.3,
			ease: "power3.out",
			showDuration: 0.3,
			hideDuration: 0.2,
			hideScale: 0.8,
			useFixedPosition: false, // New option for scrollable containers
			...options,
		};

		this.target = this.container.querySelector(this.options.targetSelector);

		if (!this.target) {
			console.warn("MouseFollower: Target element not found");
			return;
		}

		this.isActive = false;
		this.targetWidth = 0;
		this.targetHeight = 0;

		this.init();
	}

	init() {
		// Defer dimension calculation to ensure element is rendered
		requestAnimationFrame(() => {
			this.updateTargetDimensions();
		});

		const positionType = this.options.useFixedPosition ? "fixed" : "absolute";

		gsap.set(this.target, {
			position: positionType,
			top: 0,
			left: 0,
			pointerEvents: "none",
			opacity: 0,
			x: 0,
			y: 0,
			zIndex: 20,
		});

		this.xTo = gsap.quickTo(this.target, "x", {
			duration: this.options.duration,
			ease: this.options.ease,
		});
		this.yTo = gsap.quickTo(this.target, "y", {
			duration: this.options.duration,
			ease: this.options.ease,
		});

		this.bindEvents();
	}

	updateTargetDimensions() {
		const rect = this.target.getBoundingClientRect();
		this.targetWidth = rect.width;
		this.targetHeight = rect.height;
	}

	bindEvents() {
		this.handleEnter = this.handleEnter.bind(this);
		this.handleLeave = this.handleLeave.bind(this);
		this.handleMouseMove = this.handleMouseMove.bind(this);

		this.container.addEventListener("mouseenter", this.handleEnter);
		this.container.addEventListener("mouseleave", this.handleLeave);
		this.container.addEventListener("mousemove", this.handleMouseMove);
	}

	getPosition(e) {
		if (!this.targetWidth || !this.targetHeight) {
			this.updateTargetDimensions();
		}

		if (this.options.useFixedPosition) {
			return {
				x: e.clientX - this.targetWidth / 2 + this.options.cursorOffsetX,
				y: e.clientY - this.targetHeight / 2 + this.options.cursorOffsetY,
			};
		}

		const containerRect = this.container.getBoundingClientRect();

		return {
			x:
				e.clientX -
				containerRect.left +
				this.container.scrollLeft -
				this.targetWidth / 2 +
				this.options.cursorOffsetX,
			y:
				e.clientY -
				containerRect.top +
				this.container.scrollTop -
				this.targetHeight / 2 +
				this.options.cursorOffsetY,
		};
	}

	handleEnter(e) {
		this.isActive = true;

		// Update dimensions on enter in case they changed
		this.updateTargetDimensions();

		const { x, y } = this.getPosition(e);

		gsap.set(this.target, { x, y });

		gsap.to(this.target, {
			opacity: 1,
			scale: 1,
			duration: this.options.showDuration,
			ease: "power2.out",
		});
	}

	handleLeave() {
		this.isActive = false;

		gsap.to(this.target, {
			opacity: 0,
			scale: this.options.hideScale,
			duration: this.options.hideDuration,
			ease: "power2.in",
		});
	}

	handleMouseMove(e) {
		if (!this.isActive) return;

		const { x, y } = this.getPosition(e);

		this.xTo(x);
		this.yTo(y);
	}

	destroy() {
		this.container.removeEventListener("mouseenter", this.handleEnter);
		this.container.removeEventListener("mouseleave", this.handleLeave);
		this.container.removeEventListener("mousemove", this.handleMouseMove);
		gsap.killTweensOf(this.target);
		gsap.set(this.target, { opacity: 0 });
	}

	// Static method to initialize all instances on the page
	static initAll(options = {}) {
		const containers = document.querySelectorAll(".js-mouse-follow-parent");

		if (!containers.length) return [];

		const instances = [];

		containers.forEach((container) => {
			const offsetX = parseFloat(container.dataset.followOffsetX);
			const offsetY = parseFloat(container.dataset.followOffsetY);
			const duration = parseFloat(container.dataset.followDuration);
			const useFixed = container.dataset.followFixed === "true";

			const instanceOptions = {
				...options,
				...(Number.isFinite(offsetX) && { cursorOffsetX: offsetX }),
				...(Number.isFinite(offsetY) && { cursorOffsetY: offsetY }),
				...(Number.isFinite(duration) && { duration: duration }),
				...(useFixed && { useFixedPosition: true }),
			};

			instances.push(new MouseFollower(container, instanceOptions));
		});

		return instances;
	}
}

// const initLoadingBar = (stage) => {
// 	const loadingBar = document.querySelector(".global-loading-bar");
// 	const action =
// 		loadingBar.style.opacity == 1 && stage != "done" ? "move" : stage;

// 	switch (action) {
// 		case "start":
// 			loadingBar.style.opacity = 1;
// 			loadingBar.style.width =
// 				(window.innerWidth * (Math.floor(Math.random() * 6) + 1)) / 10 + "px";
// 			break;

// 		case "move":
// 			loadingBar.style.opacity = 1;
// 			loadingBar.style.width =
// 				loadingBar.getBoundingClientRect().width * 1.15 + "px";
// 			break;

// 		case "done":
// 			loadingBar.style.width = window.innerWidth + "px";

// 			setTimeout(function () {
// 				loadingBar.style.opacity = 0;
// 			}, 400);

// 			setTimeout(function () {
// 				loadingBar.style.width = 0;
// 			}, 800);
// 			break;
// 	}
// };

// ***START: ComponentSlider***

/**
 * SliderIndicator
 * Creates a mouse-following indicator that rotates based on prev/next areas
 */
class SliderIndicator {
	constructor(container, options = {}) {
		this.container =
			typeof container === "string"
				? document.querySelector(container)
				: container;

		if (!this.container) {
			console.warn("SliderIndicator: Container element not found");
			return;
		}

		// Skip initialization on touch devices
		if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
			return;
		}

		this.options = {
			prevSelector: ".js-embla-area-prev",
			nextSelector: ".js-embla-area-next",
			indicatorSelector: ".js-embla-indicator",
			prevRotation: 180,
			nextRotation: 0,
			onPrevClick: null,
			onNextClick: null,
			...options,
		};

		this.prevArea = this.container.querySelector(this.options.prevSelector);
		this.nextArea = this.container.querySelector(this.options.nextSelector);
		this.indicator = this.container.querySelector(
			this.options.indicatorSelector
		);

		if (!this.prevArea || !this.nextArea || !this.indicator) {
			console.warn("SliderIndicator: Required elements not found", {
				prevArea: this.prevArea,
				nextArea: this.nextArea,
				indicator: this.indicator,
			});
			return;
		}

		this.isActive = false;
		this.currentArea = null;

		this.init();
	}

	init() {
		const rect = this.indicator.getBoundingClientRect();
		this.offsetX = rect.width * 0.5;
		this.offsetY = rect.height * 0.5;

		gsap.set(this.indicator, {
			position: "fixed",
			top: 0,
			left: 0,
			pointerEvents: "none",
			opacity: 0,
			x: 0,
			y: 0,
			zIndex: 20,
		});

		// Create quickTo for smooth mouse following using transforms
		this.xTo = gsap.quickTo(this.indicator, "x", {
			duration: 0.3,
			ease: "power3.out",
		});
		this.yTo = gsap.quickTo(this.indicator, "y", {
			duration: 0.3,
			ease: "power3.out",
		});

		this.bindEvents();
	}

	bindEvents() {
		this.prevArea.addEventListener("mouseenter", (e) =>
			this.handleAreaEnter("prev", e)
		);
		this.prevArea.addEventListener("mouseleave", () => this.handleAreaLeave());
		this.prevArea.addEventListener("mousemove", (e) => this.handleMouseMove(e));
		this.prevArea.addEventListener("click", () => this.options.onPrevClick?.());

		this.nextArea.addEventListener("mouseenter", (e) =>
			this.handleAreaEnter("next", e)
		);
		this.nextArea.addEventListener("mouseleave", () => this.handleAreaLeave());
		this.nextArea.addEventListener("mousemove", (e) => this.handleMouseMove(e));
		this.nextArea.addEventListener("click", () => this.options.onNextClick?.());
	}

	getPosition(e) {
		return {
			x: e.clientX - this.offsetX,
			y: e.clientY - this.offsetY,
		};
	}

	handleAreaEnter(area, e) {
		this.isActive = true;
		this.currentArea = area;

		const { x, y } = this.getPosition(e);

		// Set position immediately using transforms
		gsap.set(this.indicator, { x, y });

		const rotation =
			area === "prev" ? this.options.prevRotation : this.options.nextRotation;

		gsap.to(this.indicator, {
			opacity: 1,
			rotation,
			scale: 1,
			duration: 0.3,
			ease: "power2.out",
		});
	}

	handleAreaLeave() {
		this.isActive = false;
		this.currentArea = null;

		gsap.to(this.indicator, {
			opacity: 0,
			scale: 0.8,
			duration: 0.2,
			ease: "power2.in",
		});
	}

	handleMouseMove(e) {
		if (!this.isActive) return;

		const { x, y } = this.getPosition(e);

		this.xTo(x);
		this.yTo(y);
	}
}

class ComponentSlider extends HTMLElement {
	constructor() {
		super();
		this.viewportNode = this.querySelector(".js-embla-viewport");
		this.containerNode = this.querySelector(".js-embla-container");
		this.prevButtonNode = this.querySelector(".js-embla-button-prev");
		this.nextButtonNode = this.querySelector(".js-embla-button-next");
		this.dotsNode = this.querySelector(".js-embla-dots");
		this.thumbNode = this.querySelector(".js-embla-thumbs");
		this.counterNode = this.querySelector(".js-embla-counter");
		this.progressBarNode = this.querySelector(".js-embla-progress-bar");
		this.isVertical = this.dataset.emblaVertical === "true";

		// A card slider is only ever revealed on hover or focus (see
		// _c-item-card-2025.scss), and a collection page carries one per card, so
		// building them all up front costs a long main-thread task plus a forced
		// layout each. Those opt out with data-embla-lazy and build on first
		// approach instead. Every other slider keeps its original timing.
		this.isLazy = this.hasAttribute("data-embla-lazy");
		if (!this.isLazy) {
			this.initializeEmbla();
		}
	}

	initializeEmbla() {
		if (this.isInitialized) return;
		this.isInitialized = true;

		// Card sliders ship every slide after the first in a <template> (see
		// snippets/c-item-card.liquid). Embla takes each container child as a
		// slide, so they have to be in place before it starts.
		const lazySlidesTemplate = this.containerNode.querySelector(
			":scope > template.js-embla-lazy-slides"
		);
		lazySlidesTemplate?.replaceWith(lazySlidesTemplate.content);

		// Plugins
		this.enableFade = this.dataset.emblaFade === "true";
		this.enableAutoHeight = this.dataset.emblaAutoHeight === "true";
		this.enableAutoplay = this.dataset.emblaAutoplay === "true";
		this.enableWheelGestures = this.dataset.wheelGestures === "true";

		const autoplayDelay = this.hasAttribute("data-embla-delay")
			? parseInt(this.dataset.emblaDelay)
			: 4000;
		// Reduced motion keeps the slider still; dots and buttons still work.
		this.prefersReducedMotion = window.matchMedia(
			"(prefers-reduced-motion: reduce)"
		).matches;
		const autoplay = EmblaCarouselAutoplay({
			playOnInit: this.enableAutoplay && !this.prefersReducedMotion,
			delay: autoplayDelay,
			stopOnInteraction: false,
			// Background sliders sit under the cursor most of the time, so they opt
			// out of pausing on hover (c-slider autoplay_stop_on_hover: false).
			stopOnMouseEnter: this.dataset.emblaStopOnHover !== "false",
		});
		this.enableAutoScroll = this.dataset.emblaAutoScroll === "true";
		const autoScrollSpeed = this.hasAttribute("data-embla-scroll-speed")
			? parseInt(this.dataset.emblaScrollSpeed)
			: 2;
		const autoScrollDelay = this.hasAttribute("data-embla-scroll-delay")
			? parseInt(this.dataset.emblaScrollDelay)
			: 0;
		const autoScrollDirection =
			this.dataset.emblaScrollBackward === "true" ? "backward" : "forward";
		const autoScroll = EmblaCarouselAutoScroll({
			playOnInit: this.enableAutoScroll,
			speed: autoScrollSpeed,
			startDelay: autoScrollDelay,
			direction: autoScrollDirection,
			stopOnInteraction: false,
			stopOnMouseEnter: true,
		});
		const plugins = [
			EmblaCarouselClassNames(),
			...(window.matchMedia("(any-hover: none)").matches ||
			this.enableWheelGestures
				? [EmblaCarouselWheelGestures()]
				: []),
			...(this.enableAutoplay ? [autoplay] : []),
			...(this.enableAutoScroll ? [autoScroll] : []),
			...(this.enableFade ? [EmblaCarouselFade()] : []),
			...(this.enableAutoHeight ? [EmblaCarouselAutoHeight()] : []),
		];

		this.isActive =
			this.containerNode.scrollWidth > this.containerNode.offsetWidth;

		if (!this.isActive) {
			this.classList.add("is-inactive");
		}

		const options = {
			axis: this.isVertical ? "y" : "x",
			watchDrag: this.isActive,
			...JSON.parse(this.dataset.emblaSlider.replace(/'/g, '"').trim()),
		};

		// Init Embla
		this.embla = EmblaCarousel(this.viewportNode, options, plugins);
		// destroy embla if contains only one item
		this.slideNodes = this.embla.slideNodes();
		if (this.slideNodes.length === 1) {
			this.classList.add("is-inactive");
			this.prevButtonNode?.setAttribute("disabled", "disabled");
			this.nextButtonNode?.setAttribute("disabled", "disabled");
			this.embla.destroy();
		} else if (this.isLazy) {
			// Load only the slide on show and its neighbours, rather than every
			// slide the moment the card is hovered.
			this.unveilNearbySlides();
			this.embla.on("select", this.unveilNearbySlides.bind(this));
		}

		// Initialize clickable area function
		if (this.dataset.clickableArea === "true") {
			this.initClickableAreas();
		}

		this.embla.on("resize", this.toggleActiveState.bind(this));
	}

	connectedCallback() {
		if (this.isLazy) {
			this.bindLazyInitialization();
			return;
		}
		this.initializeNavigation();
		if (window.Shopify?.designMode) {
			this.bindThemeEditorEvents();
		}
	}

	// Theme editor: selecting a block that is a slide jumps to it and holds it
	// there until the block is deselected.
	bindThemeEditorEvents() {
		const getSelectedSlideIndex = (event) => {
			// A single slide destroys Embla in initializeEmbla.
			if (!this.embla || !this.slideNodes || this.slideNodes.length < 2)
				return -1;
			return this.slideNodes.indexOf(event.target.closest(".embla__slide"));
		};

		this.addEventListener("shopify:block:select", (event) => {
			const slideIndex = getSelectedSlideIndex(event);
			if (slideIndex < 0) return;
			this.embla.scrollTo(slideIndex, true);
			this.embla.plugins().autoplay?.stop();
		});

		this.addEventListener("shopify:block:deselect", (event) => {
			if (getSelectedSlideIndex(event) < 0 || this.prefersReducedMotion) return;
			this.embla.plugins().autoplay?.play();
		});
	}

	bindLazyInitialization() {
		// pointerover rather than pointerenter: it bubbles, so one listener on the
		// card covers the thumbnail and everything inside it. focusin keeps the
		// keyboard path working, since :focus-within reveals the slider too.
		const hostElement = this.closest(".c-item-card") || this;
		this.lazyInitializationController = new AbortController();

		const startEmbla = (event) => {
			this.lazyInitializationController.abort();
			this.initializeEmbla();
			this.initializeNavigation();
			if (
				this.hasAttribute("data-card-hover-swap") &&
				this.slideNodes.length > 1
			) {
				this.bindCardHoverSwap(event);
			}
		};

		const listenerOptions = {
			signal: this.lazyInitializationController.signal,
			once: true,
		};
		hostElement.addEventListener("pointerover", startEmbla, listenerOptions);
		hostElement.addEventListener("focusin", startEmbla, listenerOptions);
	}

	// Desktop card hover (data-card-hover-swap, snippets/c-item-card.liquid):
	// entering the card crossfades to slide 2, the hover image, and leaving fades
	// back to slide 1. Once the shopper moves the slider themselves (an arrow or a
	// drag), the card stops swapping and stays on the slide they left it on,
	// until they browse back to slide 1.
	// Touch and narrow screens keep the plain swipe slider.
	bindCardHoverSwap(startEvent) {
		const card = this.closest(".c-item-card");
		const hoverImage = this.slideNodes[1].querySelector("img");
		if (!card || !hoverImage) return;

		const desktopPointerQuery = window.matchMedia(
			"(hover: hover) and (pointer: fine) and (min-width: 901px)"
		);
		let isHovered = false;
		let hasInteracted = false;
		let isSwapping = false;

		// Embla emits select synchronously from scrollTo, so a select outside
		// crossfadeTo below is the shopper's. Browsing back to the first slide
		// hands the card back to the hover swap.
		this.embla.on("select", () => {
			if (!isSwapping) hasInteracted = this.embla.selectedScrollSnap() !== 0;
		});

		// Slide 2 is deferred until the slider is built, so the first hover has to
		// wait for it; swapping straight away would show an empty slide.
		const isHoverImageReady = async () => {
			if (!hoverImage.classList.contains("lazyloaded")) {
				await new Promise((resolve) =>
					hoverImage.addEventListener("lazyloaded", resolve, { once: true })
				);
			}
			try {
				await hoverImage.decode();
				return true;
			} catch {
				return false;
			}
		};

		// Jump, then fade the outgoing slide out on top of the incoming one, shifted
		// over it for the length of the fade. The jump lands on Embla's next
		// animation frame, which emits settle just before it moves the container,
		// so the shift and the fade start in that same frame. It is the real slide,
		// already painted, so nothing flashes in between (a cloned <img> could
		// paint a frame late). The incoming slide is opaque underneath throughout,
		// so there is no dip to the placeholder background mid-fade.
		let stopCrossfade = null;
		const crossfadeTo = (index) => {
			stopCrossfade?.();
			const outgoingSlide = this.slideNodes[this.embla.selectedScrollSnap()];
			const offset =
				this.slideNodes[index].offsetLeft - outgoingSlide.offsetLeft;
			let animation = null;

			const startFade = () => {
				this.embla.off("settle", startFade);
				outgoingSlide.classList.add("is-crossfading");
				outgoingSlide.style.transform = `translateX(${offset}px)`;
				animation = outgoingSlide.animate([{ opacity: 1 }, { opacity: 0 }], {
					duration: 200,
					easing: "ease",
				});
				animation.onfinish = stop;
			};
			const stop = () => {
				this.embla.off("settle", startFade);
				animation?.cancel();
				outgoingSlide.classList.remove("is-crossfading");
				outgoingSlide.style.removeProperty("transform");
				if (stopCrossfade === stop) stopCrossfade = null;
			};

			if (!this.prefersReducedMotion) {
				stopCrossfade = stop;
				this.embla.on("settle", startFade);
			}

			isSwapping = true;
			this.embla.scrollTo(index, true);
			isSwapping = false;
		};

		const showHoverImage = async (event) => {
			if (event.pointerType !== "mouse" || !desktopPointerQuery.matches) return;
			isHovered = true;
			if (hasInteracted) return;
			if (!(await isHoverImageReady())) return;
			if (!isHovered || hasInteracted || this.embla.selectedScrollSnap() !== 0)
				return;
			crossfadeTo(1);
		};

		const showDefaultImage = (event) => {
			if (event.pointerType !== "mouse") return;
			isHovered = false;
			if (hasInteracted || this.embla.selectedScrollSnap() !== 1) return;
			crossfadeTo(0);
		};

		card.addEventListener("pointerenter", showHoverImage);
		card.addEventListener("pointerleave", showDefaultImage);
		// The pointerover that built this slider came from a pointer already in the
		// card, whose pointerenter may have gone before these listeners existed.
		if (startEvent?.type === "pointerover") showHoverImage(startEvent);
	}

	unveilNearbySlides() {
		const selectedIndex = this.embla.selectedScrollSnap();
		[selectedIndex - 1, selectedIndex, selectedIndex + 1].forEach((index) => {
			this.slideNodes[index]
				?.querySelectorAll(".js-lazy-deferred")
				.forEach((image) => unveilDeferredImage(image));
		});
	}

	initClickableAreas() {
		const prevArea = this.querySelector(".js-embla-area-prev");
		const nextArea = this.querySelector(".js-embla-area-next");
		const indicator = this.querySelector(".js-embla-indicator");

		if (!prevArea || !nextArea || !indicator) return;

		this.sliderIndicator = new SliderIndicator(this, {
			prevSelector: ".js-embla-area-prev",
			nextSelector: ".js-embla-area-next",
			indicatorSelector: ".js-embla-indicator",
			onPrevClick: () => this.embla.scrollPrev(),
			onNextClick: () => this.embla.scrollNext(),
		});
	}

	// Prev & Next Buttons
	addTogglePrevNextBtnsActive = (emblaApi, prevBtn, nextBtn) => {
		const togglePrevNextBtnsState = () => {
			if (emblaApi.canScrollPrev()) prevBtn.removeAttribute("disabled");
			else prevBtn.setAttribute("disabled", "disabled");

			if (emblaApi.canScrollNext()) nextBtn.removeAttribute("disabled");
			else nextBtn.setAttribute("disabled", "disabled");
		};

		emblaApi
			.on("select", togglePrevNextBtnsState)
			.on("init", togglePrevNextBtnsState)
			.on("reInit", togglePrevNextBtnsState);

		return () => {
			prevBtn.removeAttribute("disabled");
			nextBtn.removeAttribute("disabled");
		};
	};
	addPrevNextBtnsClickHandlers = (emblaApi, prevBtn, nextBtn) => {
		const scrollPrev = () => {
			emblaApi.scrollPrev();
		};
		const scrollNext = () => {
			emblaApi.scrollNext();
		};
		prevBtn.addEventListener("click", scrollPrev, true);
		nextBtn.addEventListener("click", scrollNext, true);

		const removeTogglePrevNextBtnsActive = this.addTogglePrevNextBtnsActive(
			emblaApi,
			prevBtn,
			nextBtn
		);

		return () => {
			removeTogglePrevNextBtnsActive();
			prevBtn.removeEventListener("click", scrollPrev, true);
			nextBtn.removeEventListener("click", scrollNext, true);
		};
	};

	// Dots
	addDotBtnsAndClickHandlers = (emblaApi, dotsNode) => {
		let dotNodes = [];

		const addDotBtnsWithClickHandlers = () => {
			dotsNode.innerHTML = emblaApi
				.scrollSnapList()
				.map(
					() =>
						'<button class="embla__dot c-slider__dot" type="button" aria-label="Go to slide"></button>'
				)
				.join("");

			const scrollTo = (index) => {
				emblaApi.scrollTo(index);
			};

			dotNodes = Array.from(dotsNode.querySelectorAll(".embla__dot"));
			dotNodes.forEach((dotNode, index) => {
				dotNode.addEventListener("click", () => scrollTo(index), false);
			});
		};

		const toggleDotBtnsActive = () => {
			const previous = emblaApi.previousScrollSnap();
			const selected = emblaApi.selectedScrollSnap();
			dotNodes[previous].classList.remove("embla__dot--selected");
			dotNodes[selected].classList.add("embla__dot--selected");
		};

		emblaApi
			.on("init", addDotBtnsWithClickHandlers)
			.on("reInit", addDotBtnsWithClickHandlers)
			.on("init", toggleDotBtnsActive)
			.on("reInit", toggleDotBtnsActive)
			.on("select", toggleDotBtnsActive);

		return () => {
			dotsNode.innerHTML = "";
		};
	};

	// "current / total" counter (snippets/c-slider.liquid, enable_counter)
	addSlideCounter = (emblaApi, counterNode) => {
		const currentNode = counterNode.querySelector(".js-embla-counter-current");
		const totalNode = counterNode.querySelector(".js-embla-counter-total");
		// The total is the image count, not the snap count. The current index uses
		// selectedScrollSnap() rather than slidesInView(), which is backed by an
		// IntersectionObserver and reports stale values while the gallery is
		// off-screen or covered by the lightbox.
		const updateCounter = () => {
			const total = emblaApi.slideNodes().length;
			const digits = String(total).length;
			if (totalNode) totalNode.textContent = total;
			// Reserve the total's width so the counter never reflows at 9 -> 10, and
			// zero-pad the value to fill it ("01 / 10"). padStart is a no-op below
			// 10 slides. Read by .c-slider__counter__current.
			counterNode.style.setProperty("--counter-digits", digits);
			if (currentNode)
				rollCounterValue(
					currentNode,
					String(emblaApi.selectedScrollSnap() + 1).padStart(digits, "0")
				);
		};

		emblaApi
			.on("init", updateCounter)
			.on("reInit", updateCounter)
			.on("select", updateCounter);
		updateCounter();
	};

	// Slide-progress bar (snippets/c-slider.liquid, enable_progress). The fill is
	// the share of slides reached, so the first slide already shows 1/N — same
	// "snap index over slide count" pairing the counter above renders as text.
	addSlideProgress = (emblaApi, progressBarNode) => {
		const updateProgress = () => {
			const total = emblaApi.slideNodes().length;
			if (!total) return;
			const progress = (emblaApi.selectedScrollSnap() + 1) / total;
			progressBarNode.style.transform = `scaleX(${progress})`;
		};

		emblaApi
			.on("init", updateProgress)
			.on("reInit", updateProgress)
			.on("select", updateProgress);
		updateProgress();
	};

	// Thumbnail navigation
	initializeThumbnailsNavigation() {
		this.viewportThumbNode = this.thumbNode.querySelector(
			".js-embla-thumbs-viewport"
		);
		this.emblaThumb = EmblaCarousel(this.viewportThumbNode, {
			align: "start",
			dragFree: true,
			inViewThreshold: 1,
		});
		this.addThumbBtnsClickHandlers();
		this.toggleThumbBtnsActive();
		this.getHiddenThumbsCount();
	}

	addThumbBtnsClickHandlers() {
		const slidesThumbs = this.emblaThumb.slideNodes();
		const scrollToIndex = slidesThumbs.map(
			(_, index) => () => this.embla.scrollTo(index)
		);
		slidesThumbs.forEach((slideNode, index) => {
			slideNode.addEventListener("click", scrollToIndex[index], false);
		});

		this.removeThumbBtnsClickHandlers = () => {
			slidesThumbs.forEach((slideNode, index) => {
				slideNode.removeEventListener("click", scrollToIndex[index], false);
			});
		};
	}

	toggleThumbBtnsActive() {
		const slidesThumbs = this.emblaThumb.slideNodes();
		const toggleThumbBtnsState = () => {
			this.emblaThumb.scrollTo(this.embla.selectedScrollSnap());
			const previous = this.embla.previousScrollSnap();
			const selected = this.embla.selectedScrollSnap();
			slidesThumbs[previous].classList.remove("is-selected");
			slidesThumbs[selected].classList.add("is-selected");
		};

		this.embla.on("select", toggleThumbBtnsState);
		this.emblaThumb.on("init", toggleThumbBtnsState);

		this.removeToggleThumbBtnsActive = () => {
			const selected = this.embla.selectedScrollSnap();
			slidesThumbs[selected].classList.remove("is-selected");
		};
	}

	// Function to display the "+X" count after visible thumbnails
	getHiddenThumbsCount() {
		const containerThumbNode = this.thumbNode.querySelector(
			".js-embla-thumbs-container"
		);
		const thumbWidth = this.emblaThumb
			.slideNodes()[0]
			.getBoundingClientRect().width;
		const wrapperWidth = containerThumbNode.getBoundingClientRect().width;
		const totalContentWidth = containerThumbNode.scrollWidth;

		if (wrapperWidth == totalContentWidth) {
			this.thumbNode.classList.add("hide-after");
			return;
		}

		const updateThumbCount = () => {
			setTimeout(() => {
				const transformMatrix = getComputedStyle(containerThumbNode).transform;
				const matrix = new DOMMatrix(transformMatrix);
				const transformX = Math.abs(matrix.m41);

				const hiddenWidth = totalContentWidth - (transformX + wrapperWidth);
				const thumbsHidden = Math.round(hiddenWidth / thumbWidth);

				if (thumbsHidden > 0) {
					this.thumbNode.classList.remove("hide-after");
					this.thumbNode.dataset.thumbCount = thumbsHidden;
				} else {
					this.thumbNode.classList.add("hide-after");
				}
			}, 800);
		};

		// Initial set
		updateThumbCount();

		// Update on pointer release
		this.emblaThumb.on("pointerUp", updateThumbCount);
		this.emblaThumb.on("select", updateThumbCount);
	}

	initializeNavigation = () => {
		if (!this.embla) return;
		if (this.slideNodes.length === 1) return;
		if (this.counterNode) {
			this.addSlideCounter(this.embla, this.counterNode);
		}
		if (this.progressBarNode) {
			this.addSlideProgress(this.embla, this.progressBarNode);
		}
		if (this.prevButtonNode && this.nextButtonNode) {
			const removePrevNextBtnsClickHandlers = this.addPrevNextBtnsClickHandlers(
				this.embla,
				this.prevButtonNode,
				this.nextButtonNode
			);
			this.embla.on("destroy", removePrevNextBtnsClickHandlers);
		}
		if (this.dotsNode) {
			const removeDotBtnsAndClickHandlers = this.addDotBtnsAndClickHandlers(
				this.embla,
				this.dotsNode
			);
			this.embla.on("destroy", removeDotBtnsAndClickHandlers);
		}
		if (this.thumbNode) {
			this.initializeThumbnailsNavigation();
		}
	};

	toggleActiveState() {
		// disable dragging when all items are in view
		if (
			this.isActive &&
			this.containerNode.scrollWidth <= this.containerNode.offsetWidth
		) {
			this.embla.reInit({ watchDrag: false });
			this.classList.add("is-inactive");
			this.isActive = false;
		}
		// re-enable dragging when items leave view
		else if (
			!this.isActive &&
			this.containerNode.scrollWidth > this.containerNode.offsetWidth
		) {
			this.embla.reInit({ watchDrag: true });
			this.classList.remove("is-inactive");
			this.isActive = true;
		}
	}
}
customElements.define("c-slider", ComponentSlider);
// ***END: ComponentSlider***

const initModelViewer = () => {
	if (document.querySelectorAll("model-viewer").length > 0) {
		document.querySelectorAll("model-viewer").forEach((viewer) => {
			viewer.addEventListener("load", (e) => {
				e.target.classList.add("css_fade_in");
			});
		});
	}
};

const initVimeoPlayer = () => {
	const videoIframes = document.querySelectorAll(".js-vimeo-video");

	const fetchVimeoPreviewImage = (id, iframe) => {
		const xhr = new XMLHttpRequest();
		xhr.open("GET", `https://vimeo.com/api/v2/video/${id}.json`, true);
		xhr.onload = () => {
			if (xhr.status === 200) {
				const response = JSON.parse(xhr.responseText);
				const previewImageUrl = response[0]?.thumbnail_large.replace(
					"_640",
					"_1920"
				);

				if (previewImageUrl) {
					const previewImage = document.createElement("img");
					previewImage.classList.add(
						"iframe-preview-image",
						"js-vimeo-preview-image"
					);
					previewImage.src = previewImageUrl;
					iframe.parentNode.insertBefore(previewImage, iframe);
				}
			}
		};
		xhr.send();
	};

	const setupVimeoPlayer = (iframe) => {
		const vimeoPlayer = new Vimeo.Player(iframe);

		vimeoPlayer.ready().then(() => {
			vimeoPlayer.getVideoId().then((id) => {
				fetchVimeoPreviewImage(id, iframe);

				vimeoPlayer.on("play", () => {
					const previewImage = iframe.previousElementSibling;
					if (previewImage?.classList.contains("js-vimeo-preview-image")) {
						previewImage.classList.add("is-hidden");
					}
				});
			});
		});

		if (iframe.src.includes("background=1")) {
			vimeoPlayer.on("loaded", () => vimeoPlayer.play());
		}
	};

	videoIframes.forEach(setupVimeoPlayer);
};

// snippets/c-video.liquid ships native <video> with its <source> URLs on
// data-src so a page of clips does not fetch every file at parse time.
// Promote them right before the first play(). No-op for eager heroes, which
// keep a real src — that is what lets every play() path call this blindly.
const loadLazyVideo = (video) => {
	const deferredSources = video.querySelectorAll("source[data-src]");
	if (deferredSources.length === 0) return;

	deferredSources.forEach((source) => {
		source.src = source.dataset.src;
		source.removeAttribute("data-src");
	});

	// Changing an existing <source>'s src does not restart resource selection;
	// only load() picks up the promoted URLs.
	video.load();
};

const playTargetVideo = (
	video,
	externalVideoType,
	player,
	soundOn = false,
	videoHasMuteBtn = false
) => {
	const playVideo = (video) => {
		loadLazyVideo(video);
		video.muted = !(videoHasMuteBtn && soundOn);
		video
			.play()
			.then(() => {
				// Resolves on `playing`, so the poster overlay only lifts once a
				// frame is actually available.
				video.parentElement
					.querySelector(".js-preview-image")
					?.classList.add("is-hidden");
			})
			// A clip that scrolls out before it has buffered rejects with
			// AbortError once pauseTargetVideo() lands — expected, not worth logging.
			.catch(() => {});
	};

	const playExternalVideo = (player, externalVideoType) => {
		if (externalVideoType === "vimeo") {
			player.setMuted(!(videoHasMuteBtn && soundOn));
			player.play().catch((error) => {
				if (error.name !== "PlayInterrupted") {
					console.error("Error playing video:", error);
				}
			});

			// Hide preview image on Vimeo play
			player
				.getPlayed()
				.then(function (played) {
					const allPlayerWithSameIds = document.querySelectorAll(
						`[data-video-id="${player.element.dataset.videoId}"]`
					);

					allPlayerWithSameIds.forEach((item) => {
						item.parentElement
							.querySelector(".js-preview-image")
							?.classList.add("is-hidden");
					});
				})
				.catch(function (error) {
					console.error("Failed to get Vimeo playback state:", error);
				});
		} else if (externalVideoType === "youtube") {
			// Both functions existing means the YouTube player is fully initialized.
			if (
				typeof player.playVideo == "function" &&
				typeof player.mute === "function"
			) {
				videoHasMuteBtn && soundOn ? player.unMute() : player.mute();

				player.playVideo();
			} else {
				console.warn(
					"YouTube player not fully initialized or methods unavailable."
				);
			}
		}
	};

	if (video) {
		playVideo(video);
	} else if (player) {
		playExternalVideo(player, externalVideoType);
	}
};

const pauseTargetVideo = (video, externalVideoType, player) => {
	const pauseVideo = (video) => {
		video.pause();
		video.muted = true;
	};

	const pauseExternalVideo = (player, externalVideoType) => {
		if (externalVideoType === "vimeo") {
			player.pause().catch((error) => {
				if (error.name !== "PlayInterrupted") {
					console.error("Error pausing video:", error);
				}
			});
			player.setMuted(true);
		} else if (externalVideoType === "youtube") {
			// Both functions existing means the YouTube player is fully initialized.
			if (
				typeof player.pauseVideo === "function" &&
				typeof player.mute === "function"
			) {
				player.pauseVideo();
				player.mute();
			} else {
				console.warn(
					"YouTube player not fully initialized or methods unavailable."
				);
			}
		}
	};

	if (video) {
		pauseVideo(video);
	} else if (player) {
		pauseExternalVideo(player, externalVideoType);
	}
};

const initVideo = () => {
	const handleVideoPlayback = (item, play, targetPlayer) => {
		const videoElement = item.querySelector("video");
		const externalVideo = item.querySelector(".js-external-video");
		const externalVideoType = externalVideo?.dataset.videoHost;
		const videoHasMuteBtn = item.querySelector(
			".js-video-mute.is-active, .js-video-unmute.is-active"
		);

		const soundOn = videoHasMuteBtn?.classList.contains("js-video-mute");
		if (!videoElement && !targetPlayer) {
			return;
		}

		if (play) {
			playTargetVideo(
				videoElement,
				externalVideoType,
				targetPlayer,
				soundOn,
				videoHasMuteBtn
			);
		} else {
			pauseTargetVideo(videoElement, externalVideoType, targetPlayer);
		}
	};

	const handleVideoVisibility = (entry, item, useVisibility) => {
		const isVisible = useVisibility ? entry.isVisible : entry.isIntersecting;
		const externalVideo = item.querySelector(".js-external-video");
		const externalVideoId = externalVideo?.dataset.videoId;
		const targetPlayer = allPlayersObj[externalVideoId];

		handleVideoPlayback(item, isVisible, targetPlayer);
	};

	const updateIframeAspectRatio = (item) => {
		const iframe = item.querySelector('iframe[data-video-host="vimeo"]');

		if (!iframe) return;

		const wrapper = iframe.closest(".c-video");
		const player = new Vimeo.Player(iframe);

		Promise.all([player.getVideoWidth(), player.getVideoHeight()]).then(
			function (dimensions) {
				const width = dimensions[0];
				const height = dimensions[1];
				const aspectRatio = (width / height).toFixed(2);
				if (aspectRatio < 1) {
					wrapper.classList.add("is-portrait");
				} else {
					wrapper.classList.add("is-landscape");
				}
				wrapper.style.setProperty("--aspect-ratio", aspectRatio);
			}
		);
	};

	// Single observer for regular videos
	const videoObserver = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				handleVideoVisibility(entry, entry.target, false);
			});
		},
		{ root: null, threshold: 0.4 }
	);

	// Single observer for visibility-tracked videos
	const visibilityObserver = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				handleVideoVisibility(entry, entry.target, true);
			});
		},
		{ root: null, threshold: 0.4, trackVisibility: true, delay: 150 }
	);

	// Tracked so videos removed by a colour swap can be let go.
	const observedVideos = new Set();

	const observeVideos = (scope) => {
		scope
			.querySelectorAll(".c-video:not(.disable-autoplay)")
			.forEach((item) => {
				updateIframeAspectRatio(item);
				observedVideos.add(item);
				// Observe all visibility-tracked videos
				if (item.classList.contains("use-visibility")) {
					visibilityObserver.observe(item);
				} else {
					// Observe all regular videos
					videoObserver.observe(item);
				}
			});
	};
	observeVideos(document);

	// productSwap (assets/js-shopify.js) replaces the PDP section in place.
	document.addEventListener("bandit:product-swapped", (event) => {
		observedVideos.forEach((item) => {
			if (item.isConnected) return;
			videoObserver.unobserve(item);
			visibilityObserver.unobserve(item);
			observedVideos.delete(item);
		});
		observeVideos(event.target);
	});

	const handleVideoControl = (e, action) => {
		const target = e.target.closest(`.js-video-${action}`);
		const videoContainer = target.closest(".c-video");
		const videoElement = videoContainer.querySelector("video");
		const externalVideo = videoContainer.querySelector(".js-external-video");
		const videoHasMuteBtn = videoContainer.querySelector(
			".js-video-mute.is-active, .js-video-unmute.is-active"
		);
		const soundOn = videoHasMuteBtn?.classList.contains("js-video-mute");
		const externalVideoType = externalVideo?.dataset.videoHost;
		const externalVideoId = externalVideo?.dataset.videoId;
		const targetPlayer = allPlayersObj[externalVideoId];

		if (action === "play") {
			playTargetVideo(
				videoElement,
				externalVideoType,
				targetPlayer,
				soundOn,
				videoHasMuteBtn
			);
		} else if (action === "pause") {
			pauseTargetVideo(videoElement, externalVideoType, targetPlayer);
		} else if (action === "mute" || action === "unmute") {
			if (externalVideoType === "youtube") {
				if (typeof targetPlayer.unMute == "function") {
					targetPlayer.isMuted() ? targetPlayer.unMute() : targetPlayer.mute();
				} else {
					console.warn(
						"YouTube player not fully initialized or methods unavailable."
					);
				}
			} else if (externalVideoType === "vimeo") {
				targetPlayer.getMuted().then((muted) => targetPlayer.setMuted(!muted));
			} else if (videoElement) {
				videoElement.muted = !videoElement.muted;
			}
		}

		// Toggle play/pause or mute/unmute button visibility
		if (action === "play" || action === "pause") {
			target.classList.remove("is-active");
			videoContainer
				.querySelector(`.js-video-${action === "play" ? "pause" : "play"}`)
				?.classList.add("is-active");
		} else if (action === "mute" || action === "unmute") {
			videoContainer
				.querySelector(".js-video-mute")
				?.classList.toggle("is-active", action === "unmute");
			videoContainer
				.querySelector(".js-video-unmute")
				?.classList.toggle("is-active", action === "mute");
		}
	};

	on("body", "click", ".js-video-mute", (e) => handleVideoControl(e, "mute"));
	on("body", "click", ".js-video-unmute", (e) =>
		handleVideoControl(e, "unmute")
	);
	on("body", "click", ".js-video-play", (e) => handleVideoControl(e, "play"));
	on("body", "click", ".js-video-pause", (e) => handleVideoControl(e, "pause"));

	on("body", "click", ".js-product-hero-slider-thumbnail", (e) => {
		// Note: The setFirstActiveImage function triggers this click event
		const sliderSelector = `.js-product-hero-slider${
			window.innerWidth <= 1024 ? "" : "[data-navigation='fade']"
		}`;
		const slider = e.target.closest(sliderSelector);

		// Exit if no slider or no video in the slider
		if (!slider || !slider.querySelector(".c-video")) return;

		// Pause all videos in the slider
		slider.querySelectorAll(".c-video").forEach((videoContainer) => {
			const videoElement = videoContainer.querySelector("video");
			const externalVideo = videoContainer.querySelector(".js-external-video");
			const externalVideoType = externalVideo?.dataset.videoHost;
			const externalVideoId = externalVideo?.dataset.videoId;
			const targetPlayer = externalVideoId
				? allPlayersObj[externalVideoId]
				: null;

			pauseTargetVideo(videoElement, externalVideoType, targetPlayer);

			// Reset video buttons
			videoContainer
				.querySelectorAll(
					".js-video-mute, .js-video-unmute, .js-video-play, .js-video-pause"
				)
				.forEach((button) => button.classList.remove("is-active"));
		});

		// Play the target video after a short delay
		setTimeout(() => {
			const activeVideoContainer = slider.querySelector(
				".js-slider-block.is-active .c-video"
			);
			if (!activeVideoContainer) return;

			const videoElement = activeVideoContainer.querySelector("video");
			const externalVideo =
				activeVideoContainer.querySelector(".js-external-video");
			const externalVideoType = externalVideo?.dataset.videoHost;
			const externalVideoId = externalVideo?.dataset.videoId;
			const targetPlayer = externalVideoId
				? allPlayersObj[externalVideoId]
				: null;

			playTargetVideo(videoElement, externalVideoType, targetPlayer);

			activeVideoContainer
				.querySelectorAll(".js-video-unmute, .js-video-pause")
				.forEach((button) => button.classList.add("is-active"));
		}, 100);
	});
};

const initMatch = () => {
	// Hide all inactive targets initially, and again in markup a colour swap
	// (productSwap in assets/js-shopify.js) has just put in place.
	const hideInactiveTargets = (scope) => {
		scope
			.querySelectorAll(".js-match [data-target]:not(.is-active)")
			.forEach((el) => el.setAttribute("aria-hidden", "true"));
	};
	hideInactiveTargets(document);
	document.addEventListener("bandit:product-swapped", (event) =>
		hideInactiveTargets(event.target)
	);

	const openTarget = (trigger) => {
		const matchWrapper = trigger.closest(".js-match");
		const triggerKey = trigger.dataset.trigger;

		// Toggle trigger active class
		matchWrapper
			.querySelectorAll("[data-trigger].is-active")
			.forEach((el) => el.classList.remove("is-active"));
		trigger.classList.add("is-active");

		// Toggle matching targets
		matchWrapper.querySelectorAll("[data-target]").forEach((target) => {
			const isMatch = target.dataset.target === triggerKey;
			target.classList.toggle("is-active", isMatch);
			target.setAttribute("aria-hidden", !isMatch);
		});
	};

	// Match navigation with content
	on("body", "click", ".js-match [data-trigger]", (e) => {
		const trigger = e.target.closest("[data-trigger]");
		// A combined-listing colour (data-swatch-url) belongs to a sibling
		// product, whose gallery is swapped in whole (productSwap in
		// assets/js-shopify.js, or the quick-add panel's re-fetch). This product
		// has no media for it, so switching here would blank the gallery until
		// the new one arrives.
		if (!trigger || trigger.dataset.swatchUrl) return;
		openTarget(trigger);
	});
};

const initTextFlow = () => {
	if (document.querySelectorAll("[data-text-path]").length > 0) {
		let textFlows = document.querySelectorAll("[data-text-path]");

		textFlows.forEach((item) => {
			let textFlowSection = document.querySelector(
				`[data-text-flow-section=${item.dataset.textPath}]`
			);
			let textPath = item.querySelector("textPath");
			let path = document.querySelector(textPath.getAttribute("href"));

			let pathLength = path.getTotalLength();
			let textPathLength = textPath.getComputedTextLength();
			let offset = -pathLength - 100;
			let isSticky = item.classList.contains("is-sticky");

			//set initial offset on page load
			textPath.setAttribute("startOffset", offset);

			const textFlowScroll = (textFlowSection) => {
				const scrollStart =
					window.pageYOffset + textFlowSection.getBoundingClientRect().top;

				if (!isMobile) {
					requestAnimationFrame(function () {
						let scrollCurrent = scrollY - scrollStart; // current scroll in pixels
						let scrollCurrentPerc = Math.abs(
							scrollCurrent / textFlowSection.offsetHeight
						);

						//lock textpath in place
						textPath.setAttribute(
							"startOffset",
							parseInt(offset + scrollCurrentPerc * 2 * pathLength) *
								`${isSticky ? -1 : 1.5}`
						);
					});
				}
			};

			textFlowScroll(textFlowSection);
			window.addEventListener(
				"scroll",
				() => {
					textFlowScroll(textFlowSection);
				},
				{ passive: true }
			);
		});
	}

	if (document.querySelectorAll("[data-text-path-animate]").length > 0) {
		window.addEventListener("DOMContentLoaded", () => {
			document.querySelectorAll("[data-text-path-animate]").forEach((path) => {
				let aniPath = path.querySelector("textPath");
				let aniTextPath = document.querySelector(aniPath.getAttribute("href"));
				let aniPathLength = aniTextPath.getTotalLength();
				let aniPathTextLength = 449;

				if (path.classList.contains("text-flow-10")) {
					aniPathTextLength = 207;
				} else if (path.classList.contains("text-flow-11")) {
					aniPathTextLength = 261;
				} else if (path.classList.contains("text-flow-12")) {
					aniPathTextLength = 267;
				}
				let pathAnimation = path.querySelector("animate");

				pathAnimation.setAttribute(
					"to",
					`${aniPathLength - aniPathTextLength}`
				);
				path.classList.add("is-animating");
			});
		});
	}
};

const initAccordions = () => {
	const toggleAccordion = (accordion, closeSiblings) => {
		const accordionContent = accordion.querySelector(".accordion-content");
		accordion.classList.toggle("is-expanded");

		if (closeSiblings) {
			getSiblings(accordion).forEach((item) => {
				if (item.classList.contains("is-expanded")) {
					toggleAccordion(item, false);
				}
			});
		}

		if (accordion.classList.contains("is-expanded")) {
			accordion.setAttribute("aria-expanded", true);
			accordionContent.setAttribute("aria-hidden", false);
			accordionContent.style.height = `${
				accordionContent.querySelector(".content-interior").clientHeight
			}px`;
		} else {
			accordion.setAttribute("aria-expanded", false);
			accordionContent.setAttribute("aria-hidden", true);
			accordionContent.style.height = "0px";
		}
	};

	on("body", "click", ".js-toggle-accordion", (e) => {
		const accordion = e.target.closest(".comp-accordion");
		if (!accordion) return;
		const closeSiblings = accordion.dataset.closeSibling !== "false";
		toggleAccordion(accordion, closeSiblings);
	});
};

const initTimer = () => {
	const timers = document.querySelectorAll(".comp-timer");
	const activeIntervals = new Set(); // Track intervals for cleanup

	// Helper: Get NY time offset once (reusable)
	const getNYTimeOffset = () => {
		const nyTime = new Date().toLocaleString("en-US", {
			timeZone: "America/New_York",
		});
		const nyDate = new Date(nyTime);
		const localDate = new Date();
		return nyDate.getTime() - localDate.getTime();
	};

	const timeOffset = getNYTimeOffset(); // Calculate once

	const doubleDigit = (n) => (n < 10 ? "0" : "") + n;

	const syncVideos = (loopLength, countDown, videos, timerParentEl) => {
		const now = Date.now() + timeOffset;
		const countDownCurrent = (countDown - now) / 1000; // ← ms to seconds

		videos.forEach((video, index) => {
			// A synced video outside the first section ships deferred sources and
			// would otherwise sit at readyState 0, never firing loadedmetadata.
			loadLazyVideo(video);
			const onLoadedMetadata = () => {
				const videoDuration = video.duration; // seconds
				const remainingLength = videoDuration - loopLength; // seconds

				// Timer already expired
				if (countDownCurrent <= 0) {
					video.loop = true;
					video.currentTime = 0;
					video.play();
					return;
				}

				// FINAL SEGMENT: direct entry on page load
				if (countDownCurrent <= remainingLength) {
					const finalPosition = videoDuration - countDownCurrent;
					video.currentTime = finalPosition;
					video.play();
					return;
				}

				// LOOP PHASE
				// Calculate where in the loop we should be so that when
				// countDownCurrent ticks down to exactly remainingLength,
				// video.currentTime will be exactly at loopLength (end of loop segment)
				const timeUntilTransition = countDownCurrent - remainingLength;
				const positionInLoop = loopLength - (timeUntilTransition % loopLength);
				const seekTo = positionInLoop >= loopLength ? 0 : positionInLoop;

				video.currentTime = seekTo;
				video.play();

				// Use a polling interval instead of timeupdate for reliable transition detection
				const pollInterval = setInterval(() => {
					const now = Date.now() + timeOffset;
					const currentCountdown = (countDown - now) / 1000;

					// On the last loop iteration, stop wrapping and let video play through
					if (currentCountdown <= remainingLength + loopLength) {
						// We're on the final loop — don't wrap back to 0
						// Just let it play through loopLength into the final segment

						if (currentCountdown <= remainingLength) {
							// We've crossed into the final segment — no seek needed
							clearInterval(pollInterval);
							activeIntervals.delete(pollInterval);
						}

						return; // Skip the loop wrap check
					}

					// Loop wrap — reset to 0 when hitting end of loop segment
					if (video.currentTime >= loopLength - 0.15) {
						video.currentTime = 0;
					}
				}, 100);

				// Store interval for cleanup and re-sync
				if (timerParentEl) {
					if (timerParentEl._syncPollInterval) {
						clearInterval(timerParentEl._syncPollInterval);
						activeIntervals.delete(timerParentEl._syncPollInterval);
					}
					timerParentEl._syncPollInterval = pollInterval;
				}
				activeIntervals.add(pollInterval);
			};

			if (video.readyState >= 1) {
				onLoadedMetadata();
			} else {
				video.addEventListener("loadedmetadata", onLoadedMetadata, {
					once: true,
				});
			}
		});
	};

	const runTimer = (timer, date, earlyHours, lockdownDate) => {
		const timerParentEl = timer.closest(".js-timer-wrapper");

		// Cache DOM queries
		const daysEl = timer.querySelector(".days");
		const hoursEl = timer.querySelector(".hours");
		const minutesEl = timer.querySelector(".minutes");
		const secondsEl = timer.querySelector(".seconds");

		// Early exit if required elements missing
		if (!daysEl || !hoursEl || !minutesEl || !secondsEl) {
			console.warn("Timer missing required child elements", timer);
			return;
		}

		const second = 1000;
		const minute = second * 60;
		const hour = minute * 60;
		const day = hour * 24;

		const countDown = new Date(date).getTime();
		const lockdownStartDate = new Date(lockdownDate).getTime();
		const earlyAccessThreshold = earlyHours * hour;

		// DECLARE intervalId FIRST with let
		let intervalId;
		let syncedVideos = null;
		let syncLoopLength = null;

		const updateTimer = () => {
			const now = Date.now() + timeOffset;
			const distance = countDown - now;

			// Timer expired
			if (distance < 1000) {
				timerParentEl?.classList.remove("timer-is-early-access");
				timerParentEl?.classList.add("timer-is-complete");
				timer.classList.add("is-complete");

				// Specifically for changing header to original header during lockdown phase
				if (root.classList.contains("is-lockdown")) {
					root.classList.add("is-countdown-complete");
				}

				if (timer.hasAttribute("data-offer-end")) {
					timer.innerHTML = timer.dataset.offerEnd;
				}

				if (timer.hasAttribute("data-offer-refresh")) {
					if (root.classList.contains("is-lockdown")) {
						root.classList.add("is-html-fade-out");
						setTimeout(() => {
							root.classList.remove("is-lockdown");
							afterFooter.init().then(() => {
								root.classList.remove("is-html-fade-out");
								root.classList.add("is-html-fade-in");
							});
						}, 300);
					} else if (!timer.classList.contains("is-complete")) {
						setTimeout(() => location.reload(), 1000);
					}
				}

				// Release synced videos to native autoplay loop
				if (syncedVideos?.length > 0) {
					syncedVideos.forEach((video) => {
						const clone = video.cloneNode(true);
						video.parentNode.replaceChild(clone, video);

						const loopStart = syncLoopLength;
						clone.currentTime = loopStart;
						clone.play().catch(() => {
							clone.muted = true;
							clone.play();
						});

						// Loop only the remaining section
						clone.addEventListener("timeupdate", () => {
							if (clone.currentTime >= clone.duration - 0.15) {
								clone.currentTime = loopStart;
							}
						});
					});
					syncedVideos = null;
				}

				// Now intervalId is accessible
				clearInterval(intervalId);
				activeIntervals.delete(intervalId);
				return;
			}

			// Lockdown starts
			if (
				lockdownStartDate &&
				now > lockdownStartDate &&
				!root.classList.contains("is-lockdown") &&
				distance > 0
			) {
				// If lockdown is starting and user is not on the homepage reload so they get directed to the appropriate page
				if (siteUrl != location.origin) {
					return location.reload();
				}

				root.classList.add("is-html-fade-out");
				setTimeout(() => {
					root.classList.add("is-lockdown");
					afterFooter.init().then(() => {
						root.classList.remove("is-html-fade-out");
						root.classList.add("is-html-fade-in");
					});
				}, 300);
			}

			// Update display
			const days = doubleDigit(Math.floor(distance / day));
			const hours = doubleDigit(Math.floor((distance % day) / hour));
			const minutes = doubleDigit(Math.floor((distance % hour) / minute));
			const seconds = doubleDigit(Math.floor((distance % minute) / second));

			daysEl.textContent = days;
			hoursEl.textContent = hours;
			minutesEl.textContent = minutes;
			secondsEl.textContent = seconds;

			// Early access check
			if (distance > 0 && distance < earlyAccessThreshold) {
				timerParentEl?.classList.add("timer-is-early-access");
			}
		};

		// Initial update
		updateTimer();

		// Video sync if available
		if (timer.hasAttribute("data-sync-video")) {
			const loopLength = parseInt(timer.dataset.syncVideo);
			syncLoopLength = loopLength;
			const videos = timerParentEl?.querySelectorAll("video");

			if (videos?.length > 0) {
				syncedVideos = videos;
				syncVideos(loopLength, countDown, videos, timerParentEl);

				// Re-sync videos when returning from background tab
				document.addEventListener("visibilitychange", () => {
					if (document.visibilityState === "visible") {
						const now = Date.now() + timeOffset;
						const distance = countDown - now;

						if (distance > 1000) {
							// Clear existing poll interval
							if (timerParentEl._syncPollInterval) {
								clearInterval(timerParentEl._syncPollInterval);
								activeIntervals.delete(timerParentEl._syncPollInterval);
							}

							syncVideos(loopLength, countDown, videos);
						}
					}
				});
			}
		}

		// Already expired on load: the complete state was applied by the first
		// update, so there is nothing left to tick.
		if (timer.classList.contains("is-complete")) {
			// With no end message there is nothing to show in place of the
			// digits, so drop the timer instead of fading an empty one out.
			if (!timer.hasAttribute("data-offer-end")) {
				timer.remove();
			}

			return false;
		}

		// ASSIGN the interval ID
		// run every 950ms, otherwise the timer can sometimes skip seconds
		intervalId = setInterval(updateTimer, 950);
		activeIntervals.add(intervalId);
		return true;
	};

	timers.forEach((timer) => {
		if (!timer.hasAttribute("data-offer-date")) {
			return; // Skip timers without required data
		}

		const lockdownStartDate = timer.dataset.lockdownStartDate;
		const offerDate = timer.dataset.offerDate;
		const earlyHours = parseFloat(timer.dataset.hours) || 0; // Parse as number

		// Validate dates
		if (isNaN(new Date(offerDate).getTime())) {
			console.warn("Invalid offer date", offerDate, timer);
			return;
		}

		const isRunning = runTimer(timer, offerDate, earlyHours, lockdownStartDate);

		// Activate timer display. An expired timer is only shown when it has an
		// end message to display in place of the digits.
		const dropWrapper = timer.closest(".drop-wrapper-bottom");
		if (dropWrapper) {
			dropWrapper.classList.add("is-active");
		}
		if (isRunning || timer.hasAttribute("data-offer-end")) {
			timer.classList.add("is-active");
		}
	});

	// Cleanup on page unload
	window.addEventListener("beforeunload", () => {
		activeIntervals.forEach(clearInterval);
	});
};

initTimer();

const initFields = () => {
	const fields = document.querySelectorAll(".field");

	fields.forEach((item) => {
		let input = item.querySelector(`input[type="text"],
			input[type="email"],
			input[type="tel"],
			input[type="url"],
			input[type="password"],
			input[type="file"],
			textarea`);
		let determineHasValue = () => {
			input.value
				? item.classList.add("has-value")
				: item.classList.remove("has-value");
		};

		if (input) {
			determineHasValue();
			input.addEventListener("change", determineHasValue);
			input.addEventListener("blur", determineHasValue);
		}
	});
};

const gScrollSpy = {
	elements: {
		scrollSpyParents: null,
		observerInstances: [],
	},

	isScrolling: false,

	createObserver: function (parent) {
		const triggers = parent.querySelectorAll("[data-scroll-spy-trigger]");
		const targets = parent.querySelectorAll("[data-scroll-spy-target]");

		if (!triggers.length || !targets.length) {
			console.warn("Missing triggers or targets, aborting observer setup");
			return;
		}

		// Track visible sections
		const visibleSections = new Set();

		const options = {
			root: null,
			rootMargin: "0px",
			threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
		};

		const observer = new IntersectionObserver((entries) => {
			if (this.isScrolling) return;

			entries.forEach((entry) => {
				const targetId = entry.target.getAttribute("data-scroll-spy-target");

				if (entry.isIntersecting) {
					visibleSections.add(entry.target);
				} else {
					visibleSections.delete(entry.target);
				}
			});

			// Find the section closest to the viewport center
			const activeSection = this.getClosestToCenter(visibleSections);
			if (activeSection) {
				const targetId = activeSection.getAttribute("data-scroll-spy-target");
				this.updateActiveState(triggers, targetId);
			}
		}, options);

		targets.forEach((target) => {
			observer.observe(target);
		});

		this.elements.observerInstances.push(observer);
		this.addClickHandlers(parent);
		this.setInitialActiveState(triggers, targets);
	},

	getClosestToCenter: function (visibleSections) {
		if (visibleSections.size === 0) return null;

		const viewportCenter = window.innerHeight / 2;
		let closest = null;
		let closestDistance = Infinity;

		visibleSections.forEach((section) => {
			const rect = section.getBoundingClientRect();
			// Calculate which part of the section is closest to viewport center
			const sectionCenter =
				Math.max(0, Math.min(rect.bottom, window.innerHeight)) -
				Math.max(0, rect.top);
			const visibleMiddle = Math.max(0, rect.top) + sectionCenter / 2;
			const distance = Math.abs(viewportCenter - visibleMiddle);

			if (distance < closestDistance) {
				closestDistance = distance;
				closest = section;
			}
		});

		return closest;
	},

	setInitialActiveState: function (triggers, targets) {
		const middle = window.innerHeight / 2;
		let closest = null;
		let closestDist = Infinity;

		targets.forEach((target) => {
			const rect = target.getBoundingClientRect();
			const targetMiddle = rect.top + rect.height / 2;
			const dist = Math.abs(middle - targetMiddle);
			if (dist < closestDist) {
				closestDist = dist;
				closest = target;
			}
		});

		if (closest) {
			const targetId = closest.getAttribute("data-scroll-spy-target");
			this.updateActiveState(triggers, targetId);
		}
	},

	updateActiveState: function (triggers, activeId) {
		let activeIndex = -1;
		triggers.forEach((trigger) => {
			const triggerId = trigger.getAttribute("data-scroll-spy-trigger");
			if (triggerId === activeId) {
				activeIndex = parseInt(trigger.dataset.index);
			}
		});

		if (activeIndex !== -1 && triggers.length > 0) {
			const navigation = triggers[0].closest(".js-navigation");
			if (navigation) {
				navigation.style.setProperty("--active-index", activeIndex);
			}
		}
	},

	addClickHandlers: function (parent) {
		const triggers = parent.querySelectorAll("[data-scroll-spy-trigger]");
		on("body", "click", "[data-scroll-spy-trigger]", (e) => {
			e.preventDefault();
			const trigger = e.target.closest("[data-scroll-spy-trigger]");
			const triggerId = trigger.getAttribute("data-scroll-spy-trigger");
			if (!triggerId) return;

			this.updateActiveState(triggers, triggerId);

			const target = parent.querySelector(
				`[data-scroll-spy-target="${triggerId}"]`
			);

			if (target) {
				this.isScrolling = true;

				target.scrollIntoView({
					behavior: "smooth",
					block: "start",
				});

				const enableObserver = () => {
					this.isScrolling = false;
					window.removeEventListener("scrollend", enableObserver);
				};

				if ("onscrollend" in window) {
					window.addEventListener("scrollend", enableObserver, { once: true });
				} else {
					setTimeout(() => {
						this.isScrolling = false;
					}, 800);
				}
			}
		});
	},

	setupNavigationToggle: function () {
		on("body", "click", ".js-navigation-toggle", (e) => {
			const target = e.target.closest(".js-navigation-toggle");
			const parent = target.closest(".js-navigation");

			parent.classList.toggle(
				"is-active",
				!parent.classList.contains("is-active")
			);
		});
	},

	setupOutsideClickHandler: function () {
		document.addEventListener("click", (e) => {
			const navigationElements = document.querySelectorAll(".js-navigation");
			let clickedOutside = true;

			navigationElements.forEach((element) => {
				if (element.contains(e.target)) {
					clickedOutside = false;
				}
			});

			if (clickedOutside && navigationElements.length > 0) {
				navigationElements.forEach((nav) => {
					nav.classList.remove("is-active");
				});
			}
		});
	},

	processAllElements: function () {
		if (
			!this.elements.scrollSpyParents ||
			this.elements.scrollSpyParents.length === 0
		)
			return;

		this.elements.scrollSpyParents.forEach((parent) => {
			this.createObserver(parent);
		});
	},

	destroy: function () {
		this.elements.observerInstances.forEach((observer) => {
			observer.disconnect();
		});
		this.elements.observerInstances = [];
	},

	init: function () {
		const elements = document.querySelectorAll(".js-scroll-spy-parent");

		if (elements && elements?.length > 0) {
			this.elements.scrollSpyParents = elements;
			this.processAllElements();
			this.setupNavigationToggle();
			this.setupOutsideClickHandler();
		}
	},
};

// global elements and

const initFrontpage = () => {
	//drop gate
	on("body", "click", ".js-gate-subscribe", (e) => {
		const targetParentEl = e.target.closest(".js-gate-section");
		targetParentEl.classList.add("is-active-subscribe");
		targetParentEl.querySelector('form input[type="email"]').focus();
	});

	on("body", "click", ".js-gate-subscribe-back", (e) => {
		const targetParentEl = e.target.closest(".js-gate-section");
		targetParentEl.classList.remove("is-active-subscribe");
	});
};

const initScrollDistance = () => {
	let scrollDistanceTotal = "";
	const scrollDistanceCalc = (callback, refresh = 100) => {
		// Make sure a valid callback was provided
		if (!callback || typeof callback !== "function") return;

		const y = window.scrollY;

		// Initialize starting point once per burst
		if (scrollDistanceCalc._start == null) {
			scrollDistanceCalc._start = y;
		}

		// Reset trailing timer
		if (scrollDistanceCalc._t) {
			clearTimeout(scrollDistanceCalc._t);
		}

		scrollDistanceCalc._t = setTimeout(() => {
			const start = scrollDistanceCalc._start ?? y;
			const end = window.scrollY;
			const dist = end - start;

			// Maintain original implicit global 'distance' usage
			window.distance = dist;

			try {
				// Preserve callback signature
				callback(dist, start, end);
			} finally {
				// Cleanup for next burst
				scrollDistanceCalc._start = null;
				scrollDistanceCalc._t = null;
			}
		}, refresh);
	};

	const distanceUpdate = () => {
		scrollDistanceCalc(() => {
			let scrollTotal =
				parseInt(localStorage.getItem("scrollDistance") || 0) +
				Math.abs(distance);

			// update local storage total distance
			localStorage.setItem("scrollDistance", scrollTotal);

			// Calculate converted distance
			const converted = parseFloat(scrollTotal * 0.00000026458335).toFixed(3);

			// update local storage HTML & all displays
			document.querySelectorAll(".scroll-distance-display").forEach((el) => {
				const plusEl = el.parentElement.querySelector(".scroll-distance-plus");
				const unitEl = el.parentElement.querySelector(".scroll-distance-unit");

				if (el.closest(".global-header") && converted > 99999) {
					el.textContent = "99999"; // cap display
					plusEl.classList.remove("is-hidden");
					unitEl.setAttribute("dy", "10"); // shift down "KM"
				} else {
					el.textContent = converted;
					if (plusEl) plusEl.classList.add("is-hidden");
					if (unitEl) unitEl.setAttribute("dy", "0"); // reset position
				}
			});

			scrollDistanceTotal = converted;
		}, 1000);
	};

	//leaderboard function
	const spreadsheetId = "1eViC43CFJqfCamqMkMR_eOKFq5b6GCddP00frhDFyok";
	let sortedArray = [];
	let newValue = [];
	let initialFull = "";
	const leaderboardForm = document.querySelector(".comp-leaderboard form");

	if (!leaderboardForm) return false;
	const formRank = leaderboardForm.querySelector(".entry-rank");

	const findNewEntry = (value, array) => {
		for (const item of array) {
			if (
				Array.isArray(item) &&
				item.length === value.length &&
				value.every((v, i) => item[i] === v)
			) {
				item[0] = initialFull;
				return item;
			}
		}
	};

	on("body", "click", ".js-leaderboard", (e) => {
		root.classList.add("leaderboard-is-active");

		fetch(
			"https://vs-service.vercel.app/api/googleapi/spreadsheets/values/get/",
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					spreadsheetId,
					serviceTarget: "banditrunning",
					valueRange: "Leaderboard",
				}),
			}
		)
			.then((res) => res.json())
			.then((res) => {
				//add current and sort descending
				let arr = res.data.values;

				arr.forEach((score, index) => {
					let target = document.querySelector(
						`[data-leaderboard-ticker="${index}"]`
					);

					target.querySelectorAll(".ticker-name").forEach((item) => {
						item.textContent = score[0];
					});
					target.querySelectorAll(".ticker-score").forEach((item) => {
						item.textContent = score[1];
					});
				});

				newValue = ["", scrollDistanceTotal];
				arr.push(newValue);

				sortedArray = arr.sort(([a, b], [c, d]) => {
					return a - c || d - b;
				});

				//remove last ranking (17th place)
				sortedArray.pop();
				let rank = sortedArray.indexOf(findNewEntry(newValue, sortedArray)) + 1;

				if (rank !== 0 && rank <= sortedArray.length) {
					leaderboardForm
						.querySelector(".leaderboard-bottom")
						.classList.remove("is-hidden");
					leaderboardForm.classList.add("is-active");
					formRank
						.closest(".leaderboard-entry-rank")
						.classList.remove("is-hidden");
					formRank.textContent = getIntSuffix(rank);
				} else {
					leaderboardForm
						.querySelector(".leaderboard-bottom")
						.classList.add("is-hidden");
					leaderboardForm.classList.add("is-active");
					leaderboardForm.querySelector(
						".leaderboard-entry-header"
					).textContent = "Keep Scrolling!";
					formRank
						.closest(".leaderboard-entry-rank")
						.classList.add("is-hidden");
				}
			})
			.catch((error) => {
				// Handle the error here
				console.error("Fetch error:", error);
			});
	});

	on("body", "click", ".leaderboard-entry-close", (e) => {
		leaderboardForm.classList.remove("is-active");
	});

	on("body", "click", ".leaderboard-close", (e) => {
		root.classList.add("leaderboard-is-leaving");
		setTimeout(() => {
			root.classList.remove("leaderboard-is-active");
			root.classList.remove("leaderboard-is-leaving");
		}, 1800);
	});

	on("body", "input", ".leaderboard-initial", (e) => {
		if (
			e.inputType === "deleteContentBackward" &&
			e.target.previousElementSibling &&
			e.target.previousElementSibling.nodeName === "INPUT"
		) {
			e.target.previousElementSibling.focus();
		} else if (
			e.target.value !== "" &&
			e.target.nextElementSibling &&
			e.target.nextElementSibling.nodeName === "INPUT"
		) {
			e.target.nextElementSibling.focus();
		}
	});

	on("body", "submit", "form[action=leaderboard_entry]", (e) => {
		e.preventDefault();

		//join initials
		let initialArr = [];
		e.target.querySelectorAll('input[type="text"]').forEach((letter) => {
			initialArr.push(letter.value);
		});

		initialFull = e.target.querySelector(".leaderboard-initial-full").value =
			initialArr.join("").toUpperCase();

		findNewEntry(newValue, sortedArray);

		fetch(
			`https://vs-service.vercel.app/api/googleapi/spreadsheets/values/update`,
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					spreadsheetId,
					serviceTarget: "banditrunning",
					valueRange: "Leaderboard",
					valueInputOption: "USER_ENTERED",
					resource: {
						values: sortedArray,
					},
				}),
			}
		)
			.then((res) => res.json())
			.then((res) => {
				//update new ranking
				if (sortedArray) {
					sortedArray.forEach((score, index) => {
						let target = document.querySelector(
							`[data-leaderboard-ticker="${index}"]`
						);

						target.querySelectorAll(".ticker-name").forEach((item) => {
							item.innerHTML = score[0];
						});
						target.querySelectorAll(".ticker-score").forEach((item) => {
							item.innerHTML = score[1];
						});
					});
					res.statusText == "OK"
						? leaderboardForm.classList.remove("is-active")
						: "";
				}
			});
	});

	distanceUpdate();
	window.addEventListener("scroll", distanceUpdate, { passive: true });
};

const initOldTvEffect = () => {
	const canvas = document.getElementById("c-old-tv-effect");

	if (canvas) {
		const ctx = canvas.getContext("2d");

		// Set initial canvas size
		canvas.width = 256;
		canvas.height = 256;

		// Adjust canvas size to match the container
		let resizeRafId = null;
		function resizeCanvas() {
			if (resizeRafId) return;
			resizeRafId = requestAnimationFrame(() => {
				resizeRafId = null;
				const canvasContainer = canvas.parentElement;
				const w = canvasContainer.offsetWidth;
				const h = canvasContainer.offsetHeight;
				canvas.style.width = w + "px";
				canvas.style.height = h + "px";
			});
		}
		resizeCanvas();
		window.addEventListener("resize", resizeCanvas, { passive: true });

		function generateNoise(ctx) {
			const width = ctx.canvas.width;
			const height = ctx.canvas.height;
			const imageData = ctx.createImageData(width, height);
			const data = imageData.data;

			// Generate noise with smaller grain
			for (let i = 0; i < data.length; i += 4) {
				const noiseValue = Math.random() * 100; // Smaller noise value for finer grain
				const red = noiseValue; // Slightly emphasizes red
				const green = noiseValue * 0.8; // Less green for more blue dominance
				const blue = 255; // Fixed blue channel for emphasis

				data[i] = red; // Red channel
				data[i + 1] = green; // Green channel
				data[i + 2] = blue; // Blue channel
				data[i + 3] = 255; // Alpha channel
			}

			ctx.putImageData(imageData, 0, 0);
		}

		let frame = 0;

		// Add distortion effect at the bottom
		function applyDistortion() {
			const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
			const data = imageData.data;
			const width = canvas.width;

			// Target a single line near the bottom for distortion
			const y = canvas.height - 20; // Set the distortion line to 20px from the bottom
			const distortionRange = 10; // The number of rows affected by distortion

			for (let dy = 0; dy < distortionRange; dy++) {
				const offset = Math.sin(frame * 0.1 + dy) * 2; // Light distortion
				for (let x = 0; x < canvas.width; x++) {
					const srcX = Math.min(width - 1, Math.max(0, x + offset));
					const srcIndex = (Math.floor(srcX) + (y + dy) * width) * 4;
					const destIndex = (x + (y + dy) * width) * 4;

					// Apply distortion only to the specific rows
					data[destIndex] = data[srcIndex]; // Red channel
					data[destIndex + 1] = data[srcIndex + 1]; // Green channel
					data[destIndex + 2] = data[srcIndex + 2]; // Blue channel
					data[destIndex + 3] = data[srcIndex + 3]; // Alpha channel
				}
			}

			ctx.putImageData(imageData, 0, 0);
		}

		// Stripe properties
		let stripeY = canvas.height / 2; // Initial position of the stripe
		const stripeHeight = 30; // Height of the stripe
		function drawStripe() {
			ctx.fillStyle = `rgba(128, 0, 128, 0.2)`; // Purple stripe with light opacity
			ctx.fillRect(0, stripeY - stripeHeight / 2, canvas.width, stripeHeight); // Center stripe on `stripeY`
		}

		// Combined animation for noise, stripe, and distortion
		function combinedAnimate() {
			frame++;
			if (frame % 2 === 0) {
				generateNoise(ctx);
			}
			applyDistortion();
			drawStripe();
			requestAnimationFrame(combinedAnimate);
		}

		combinedAnimate();

		// Update stripe position on mouse movement
		window.addEventListener("mousemove", (e) => {
			const mouseYPercentage = (e.clientY / window.innerHeight) * 100; // Calculate mouse Y position as a percentage of the window height
			stripeY = (mouseYPercentage / 100) * canvas.height; // Map the percentage to the canvas height
		});
	}
};

const initPhotoDistortion = () => {
	// reference: https://webglfundamentals.org/webgl/lessons/webgl-qna-create-image-warping-effect-in-webgl.html
	const imgEl = document.querySelector("[data-image]");
	if (!imgEl) return;

	const img = new Image();
	img.onload = start;
	img.src = imgEl.dataset.image;

	function start() {
		const canvas = document.getElementById("c-photo-distortion");
		const ctx = canvas.getContext("2d");

		let userStrength = 200;
		canvas.addEventListener("mousemove", (e) => {
			const rect = canvas.getBoundingClientRect();
			const y = (e.clientY - rect.top) / rect.height;
			userStrength = 100 + y * 400; // varies between 100 and 600
		});

		const resize = () => {
			const width = canvas.clientWidth;
			const height = canvas.clientHeight;
			if (width !== canvas.width || height !== canvas.height) {
				canvas.width = width;
				canvas.height = height;
			}
		};

		const mix = (a, b, l) => a + (b - a) * l;
		const upDown = (v) => Math.sin(v) * 0.5 + 0.5;

		const render = (time) => {
			time *= 0.001;
			resize();

			const t1 = time;
			const t2 = time * 0.37;
			const { height } = canvas;
			// for each line in the canvas
			for (let dstY = 0; dstY < height; ++dstY) {
				// v is value that goes 0 to 1 down the canvas
				const v = dstY / height;

				let off = 0;
				// compute some amount to offset the src

				const off1 =
					Math.sin((v + 0.5) * mix(3, 12, upDown(t1))) * userStrength;
				const off2 =
					Math.sin((v + 0.5) * mix(3, 12, upDown(t2))) * userStrength;
				off = off1 + off2;
				// compute what line of the source image we want
				// NOTE: if off = 0 then it would just be stretching
				// the image down the canvas.
				let srcY = (dstY * img.height) / height + off;
				// clamp srcY to be inside the image
				srcY = Math.max(0, Math.min(img.height - 1, srcY));
				// draw a single line from the src to the canvas
				ctx.drawImage(img, 0, srcY, img.width, 1, 0, dstY, canvas.width, 1);
			}

			requestAnimationFrame(render);
		};

		requestAnimationFrame(render);
	}
};

const productFlipCardToggleState = new Map();

const initToggle = () => {
	const getProductFlipCardStateKey = (wrapper) =>
		wrapper.closest(".product-flip-card")?.id;

	const prepareProductFlipCard = (wrapper) => {
		if (!wrapper.closest(".product-flip-card")) return;

		wrapper.querySelectorAll(".js-toggle-content").forEach((content, index) => {
			content.dataset.toggle = index + 1;
			content.classList.toggle("is-hidden", index > 1);
		});
	};

	const initToggleBg = (toggle, wrapper) => {
		const toggleWidth = toggle.offsetWidth;
		const bg = toggle.parentElement.querySelector(".js-toggle-bg");
		const content = wrapper.querySelector(
			`.js-toggle-content[data-toggle="${toggle.dataset.toggle}"]`
		);
		const image = wrapper.querySelector(
			`.js-toggle-image[data-toggle="${toggle.dataset.toggle}"]`
		);

		// reset width
		bg.style.width = toggleWidth + "px";
		// change position
		bg.style.transform = `translate(${toggle.offsetLeft}px, 0)`;

		// set active state to toggle
		toggle.parentElement.querySelector(".is-active")
			? toggle.parentElement
					.querySelector(".is-active")
					.classList.remove("is-active")
			: "";
		toggle.classList.add("is-active");

		const stateKey = getProductFlipCardStateKey(wrapper);
		if (stateKey) {
			productFlipCardToggleState.set(stateKey, toggle.dataset.toggle);
		}

		// set active state to image
		if (image) {
			getSiblings(image).forEach((element) => {
				element.classList.remove("is-active");
				element.setAttribute("aria-hidden", true);
			});
			image.classList.add("is-active");
			image.setAttribute("aria-hidden", false);
		}
		// set active state to content
		if (content) {
			getSiblings(content).forEach((element) => {
				element.classList.remove("is-active");
				element.setAttribute("aria-hidden", true);
				// reset accordion
				element.querySelector(".comp-accordion.is-expanded button")?.click();
			});
			content.classList.add("is-active");
			content.setAttribute("aria-hidden", false);
		}
	};

	const initToggleWrapper = (wrapper) => {
		prepareProductFlipCard(wrapper);

		const stateKey = getProductFlipCardStateKey(wrapper);
		const savedToggle = productFlipCardToggleState.get(stateKey) || "1";
		const trigger =
			wrapper.querySelector(`.js-toggle[data-toggle="${savedToggle}"]`) ||
			wrapper.querySelector(".toggle-left");
		trigger && initToggleBg(trigger, wrapper);
	};

	document.querySelectorAll(".js-toggles-wrapper").forEach(initToggleWrapper);

	on("body", "click", ".js-toggle", (e) => {
		const trigger = e.target.closest(".js-toggle");
		const wrapper = e.target.closest(".js-toggles-wrapper");

		trigger && wrapper && initToggleBg(trigger, wrapper);
	});

	if (window.Shopify?.designMode) {
		document.addEventListener("shopify:section:load", (event) => {
			event.target
				.querySelectorAll(".js-toggles-wrapper")
				.forEach(initToggleWrapper);
		});

		document.addEventListener("shopify:block:select", (event) => {
			const content = event.target.closest(".js-toggle-content");
			const wrapper = content?.closest(".js-toggles-wrapper");
			if (!content || !wrapper) return;

			prepareProductFlipCard(wrapper);
			const contentIndex = Array.from(
				wrapper.querySelectorAll(".js-toggle-content")
			).indexOf(content);
			const trigger = wrapper.querySelector(
				`.js-toggle[data-toggle="${contentIndex + 1}"]`
			);

			trigger && initToggleBg(trigger, wrapper);
		});
	}
};

const initImageAccordions = () => {
	// Image/Accordions Section
	on("body", "click", ".js-toggles-wrapper [data-trigger]", (e) => {
		// Works with initAccordions & initMatch
		const trigger = e.target.closest("[data-trigger]");
		const triggerKey = trigger?.dataset.trigger;
		const triggerContent = e.target.closest(".js-toggle-content");
		if (!trigger || !triggerContent) return;

		// Only proceed if no expanded item is inside
		if (triggerContent.querySelector(".is-expanded")) return;

		const matchWrapper = e.target.closest(".js-match");
		if (!matchWrapper) return;

		const toggleKey = triggerContent.dataset.toggle;
		const toggle = matchWrapper.querySelector(`[data-toggle="${toggleKey}"]`);
		if (!toggle) return;

		// Collapse toggle if active
		if (toggle.classList.contains("is-active")) toggle.click();

		// Remove "is-active" state and set aria-hidden to target
		const target = matchWrapper.querySelector(`[data-target="${triggerKey}"]`);
		const matchingTrigger = matchWrapper.querySelector(
			`[data-trigger="${triggerKey}"]`
		);

		matchingTrigger?.classList.remove("is-active");
		if (target) {
			target.classList.remove("is-active");
			target.setAttribute("aria-hidden", "true");
		}
	});
};

const initStickyBar = () => {
	// sticky bar
	let stickyBar = document.querySelector(".js-sticky-bar");
	const footer = document.querySelector(".global-footer");

	if (stickyBar && footer) {
		let observeBottom = new IntersectionObserver(
			(e) => {
				e[0].isIntersecting
					? stickyBar.classList.remove("is-visible")
					: stickyBar.classList.add("is-visible");
			},
			{
				threshold: [0, 1],
			}
		);

		observeBottom.observe(footer);
	}
};

const initPageTransition = () => {
	// Page transitions are handled natively by the cross-document View
	// Transition API (`@view-transition` in styles/__animations.scss), so
	// internal links navigate without JS interception.

	const overlayClasses = [
		"header-menu-active",
		"overlay-is-active",
		"leaderboard-is-active",
	];

	const closeOverlays = ({ immediate = false } = {}) => {
		root.classList.remove(...overlayClasses);
		closeAllDialogs({ immediate });
	};

	const findLink = (event, selector) =>
		event.target instanceof Element ? event.target.closest(selector) : null;

	// auto assign noopener and _blank to external links
	document.querySelectorAll("a[href]").forEach((link) => {
		if (/^https?:$/.test(link.protocol) && link.origin !== location.origin) {
			link.rel = "noopener";
			link.target = link.target || "_blank";
		}
	});

	// Clean href for crawlers; append variant so shopper lands on the in-stock size.
	// The href is rewritten before the browser follows it, so the native
	// navigation (and its view transition) proceeds — no preventDefault.
	const appendVariant = (event) => {
		const link = findLink(event, "a[data-variant-id]");
		if (!link || !link.dataset.variantId) return;

		const url = new URL(link.href);
		if (url.searchParams.has("variant")) return;

		url.searchParams.set("variant", link.dataset.variantId);
		link.href = url.href;
	};

	document.addEventListener("click", appendVariant);
	document.addEventListener("auxclick", appendVariant);

	// Close the menu / minicart / search the moment a link is clicked, so they
	// animate shut while the next page is fetched instead of being captured
	// open by the outgoing view transition snapshot.
	document.addEventListener("click", (event) => {
		if (event.defaultPrevented) return;
		if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
			return;
		}

		const link = findLink(event, "a[href]");
		if (!link || link.target === "_blank" || link.hasAttribute("download")) {
			return;
		}
		if (link.origin !== location.origin) return;

		// Same-page anchor: the document isn't replaced, so leave overlays alone.
		if (
			link.hash &&
			link.pathname === location.pathname &&
			link.search === location.search
		) {
			return;
		}

		closeOverlays();
	});

	// Safety net: without this a bfcache restore comes back with whatever
	// overlay state the page was frozen with.
	window.addEventListener("pagehide", () => closeOverlays({ immediate: true }));
};

const initPageAnimation = () => {
	document.querySelectorAll("[data-animate]").forEach((item) => {
		let observer = new IntersectionObserver(
			(el) => {
				if (
					el[0].isIntersecting === true &&
					!el[0].target.classList.contains("is-animated")
				) {
					el[0].target.classList.add("is-animated");
				}
			},
			{
				root: null,
				threshold: [0.2], // 0 = page start; 1 = page bottom, items only revealed when in full view
			}
		);

		observer.observe(item);
	});
	if (document.querySelectorAll('[data-animate*="letter"]').length > 0) {
		document.querySelectorAll('[data-animate*="letter"]').forEach((section) => {
			section.querySelectorAll(".letter").forEach((el, i) => {
				el.style.animationDelay = `${(0.5 + i * 0.05).toFixed(2)}s`;
			});
		});
	}
};

const initLightbox = () => {
	if (document.querySelectorAll("[data-lightbox-target]").length > 0) {
		let lightboxes = document.querySelectorAll("[data-lightbox-target]");
		lightboxes.forEach((lightbox) => {
			//check for article container then append first image in lightbox to featured image

			if (document.querySelector(".article-content")) {
				let featuredImage = document
					.querySelector(
						`[data-lightbox-id="${lightbox.dataset.lightboxTarget}"]`
					)
					.closest(".lightbox-featured-image");
				let featuredImageCloned = lightbox
					.querySelector(".lightbox-images .object-contain img")
					.cloneNode(true);

				featuredImage.prepend(featuredImageCloned);
			}

			// lightbox actions
			let lightboxTargetId = lightbox.dataset.lightboxTarget;

			//if not video get images, and allow prev next
			if (!lightbox.classList.contains("is-video")) {
				let lightBoxImages = lightbox.querySelector(".lightbox-images");
				let lightboxImagesSpans =
					lightBoxImages.querySelectorAll(".object-contain");
				let lightboxCountCurrent = lightbox.querySelector(
					".lightbox-count-current"
				);

				// assign total image count
				lightbox.querySelector(".lightbox-count-total").innerHTML =
					lightboxImagesSpans.length;
				lightboxImagesSpans[0].classList.add("is-active");

				on(
					"body",
					"click",
					`[data-lightbox-target="${lightboxTargetId}"] [data-lightbox-trigger]`,
					(e) => {
						let direction = e.target.closest("[data-lightbox-trigger]").dataset
							.lightboxTrigger;
						let imageWrapper = e.target
							.closest(".global-lightbox")
							.querySelector(".lightbox-images");
						let imageWrapperSpans =
							imageWrapper.querySelectorAll(".object-contain");
						let imageActive = imageWrapper.querySelector(
							".object-contain.is-active"
						);

						let currentActiveIndex = Array.prototype.indexOf.call(
							imageActive.parentNode.children,
							imageActive
						);

						let newActiveIndex =
							direction == "prev"
								? imageActive.previousElementSibling != null
									? currentActiveIndex - 1
									: imageWrapperSpans.length - 1
								: imageActive.nextElementSibling != null
									? currentActiveIndex + 1
									: 0;

						// assign correct active states
						imageWrapperSpans.forEach((el, i) => {
							i == newActiveIndex
								? el.classList.add("is-active")
								: el.classList.remove("is-active");
						});

						// update current count
						lightboxCountCurrent.innerText = newActiveIndex + 1;
					}
				);
			}
		});

		// Unlike the Vimeo player, which is created anew each time, we create the YouTube player only once and then reuse it. The YouTube player is identified and called by its lightboxTarget attribute
		let ytLightboxVideoPlayers = {};

		function playLightboxVideo(target) {
			if (target.classList.contains("is-video")) {
				if (target.classList.contains("is-youtube")) {
					let lightboxTarget = target.dataset.lightboxTarget;
					if (ytLightboxVideoPlayers[lightboxTarget]) {
						ytLightboxVideoPlayers[lightboxTarget].playVideo();
					} else {
						let ytVideoPlayer = new YT.Player(
							target.querySelector(".js-yt-video-defer"),
							{
								videoId:
									target.querySelector(".js-yt-video-defer").dataset.ytId,
								playerVars: {
									playsinline: 1,
								},
								events: {
									onReady: function (event) {
										event.target.playVideo();
									},
								},
							}
						);
						ytLightboxVideoPlayers[lightboxTarget] = ytVideoPlayer;
					}
				} else if (target.classList.contains("is-vimeo")) {
					const vimeoVideoPlayer = new Vimeo.Player(
						target.querySelector(".js-vimeo-video-defer")
					);
					vimeoVideoPlayer.play();
					ytLightboxVideoPlayers[target.dataset.lightboxTarget] =
						vimeoVideoPlayer;
				} else {
					const videoNative = target.querySelector("video");
					if (videoNative && (videoNative.paused || videoNative.ended)) {
						loadLazyVideo(videoNative);
						videoNative.play().catch((error) => {
							// Handle potential errors, such as a user trying to autoplay without interaction
							console.error("Playback failed:", error);
						});
					}
				}
			}
		}

		function pauseLightboxVideo(target) {
			if (target.classList.contains("is-video")) {
				if (target.classList.contains("is-youtube")) {
					ytLightboxVideoPlayers[target.dataset.lightboxTarget]?.pauseVideo();
				} else if (target.classList.contains("is-vimeo")) {
					ytLightboxVideoPlayers[target.dataset.lightboxTarget]?.pause();
				} else {
					const videoNative = target.querySelector("video");
					if (videoNative) {
						videoNative.pause();
					}
				}
			}
		}

		// Each lightbox is a c-dialog. Opening starts playback, so the trigger
		// calls openDialog() rather than going through the .js-dialog-open
		// delegate; closing (button, backdrop, Escape) is native, and the pause
		// hangs off the dialog's own `close` event.
		on("body", "click", ".js-lightbox-open", (e) => {
			const lightboxId =
				e.target.closest("[data-lightbox-id]").dataset.lightboxId;
			const target = document.querySelector(
				`[data-lightbox-target="${lightboxId}"]`
			);
			if (!target) return;

			openDialog(target.closest("dialog.c-dialog"), {
				trigger: e.target.closest(".js-lightbox-open"),
			});
			playLightboxVideo(target);
		});

		lightboxes.forEach((lightbox) => {
			lightbox
				.closest("dialog.c-dialog")
				?.addEventListener("close", () => pauseLightboxVideo(lightbox));
		});
	}
};

const initContentLayout = () => {
	let contentLayout = document.querySelector(".content-layout");
	if (!contentLayout) {
		return false;
	}

	const productWraps = contentLayout.querySelectorAll(".article-product-wrap");

	if (productWraps.length > 0) {
		productWraps.forEach((wrap) => {
			const productCards = wrap.querySelectorAll(".c-item-card");
			productCards.forEach((product) => {
				let parent = product.parentNode;
				wrap.appendChild(product);
				// select element to unwrap
				if (parent.hasAttribute("style")) {
					wrap.appendChild(product);
					parent.remove();
				}
			});
		});
	}

	// iframe fit vids
	reframe(
		'.content-layout iframe[src*="vimeo.com"], .content-layout iframe[src*="youtube.com"]',
		"video-reframe"
	);
};

const initBlogIndex = () => {
	if (document.querySelector(".blog-pagination-next")) {
		let infiniteScroll = new InfiniteScroll(".blog-load-more", {
			path: ".blog-pagination-next",
			append: ".article-card",
			button: ".blog-load-more-trigger",
			scrollThreshold: false,
			history: false,
		});

		infiniteScroll.on("append", function (body, path, items, response) {
			items.forEach((el) => {
				el.classList.add("is-animated");
			});
		});

		infiniteScroll.on("last", (body, path) => {
			let loadMoreTrigger = document.querySelector(".blog-load-more-trigger");

			setTimeout(() => {
				loadMoreTrigger.style.display = "block";
				loadMoreTrigger.disabled = true;
				loadMoreTrigger.innerHTML = "";
			}, 100);
		});
	}

	on("body", "click", "[data-blog-grid-style-trigger]", (e) => {
		let style = e.target.dataset.blogGridStyleTrigger;

		e.target.closest(".blog-grid-style-triggers").classList.toggle("is-active");

		document.querySelectorAll("[data-blog-grid-style-trigger").forEach((el) => {
			el.dataset.blogGridStyleTrigger == style
				? el.classList.add("is-active")
				: el.classList.remove("is-active");
		});

		document.querySelector(".blog-grid").setAttribute("data-style", style);
	});
};

const initMembershipPage = () => {
	const memberProduct = document.querySelector(".membership-purchase");
	if (memberProduct.querySelector(".js-item-selector-variant")) {
		const dropdowns = memberProduct.querySelectorAll(
			".js-item-selector-variant"
		);
		on("body", "click", ".js-item-selector-variant", (e) => {
			e.target
				.closest(".js-item-selector-variant")
				.classList.toggle("is-active");
		});

		dropdowns.forEach((dropdown) => {
			document.addEventListener("click", (e) => {
				if (
					dropdown.classList.contains("is-active") &&
					e.target &&
					!e.target.classList.contains("selector-trigger") &&
					!e.target.closest(".selector-trigger") &&
					!e.target.classList.contains("selector-display") &&
					!e.target.closest(".selector-display")
				) {
					dropdown.classList.remove("is-active");
				}
			});
		});

		//membership editorial
		const container = document.querySelector(".membership-editorial");
		if (container) {
			gsap.registerPlugin(ScrollTrigger);
			const pinWrap = container.querySelector(".editorial-gallery-interior");

			let pinWrapWidth;
			let horizontalScrollLength;

			function refresh() {
				pinWrapWidth = pinWrap.scrollWidth * 0.8;
				horizontalScrollLength = pinWrapWidth - window.innerWidth;
			}

			refresh();
			// Pinning and horizontal scrolling
			gsap.to(pinWrap, {
				scrollTrigger: {
					trigger: container,
					pin: container,
					scrub: 1,
					start: () => "-100 top",
					end: () => `+=${pinWrapWidth}`,
					invalidateOnRefresh: true,
				},
				x: () => -horizontalScrollLength * 1.35,
				ease: "none",
			});

			ScrollTrigger.addEventListener("refreshInit", refresh);
		}

		on("body", "click", ".membership-benefits .js-toggle-accordion", (e) => {
			isTablet
				? setTimeout(() => {
						ScrollTrigger.refresh();
					}, 800)
				: "";
		});
	}

	// membership page sticky add
	let productBar = document.querySelector(".membership-product");
	const footer = document.querySelector(".global-footer");
	if (productBar && footer) {
		let observeBottom = new IntersectionObserver(
			(e) => {
				e[0].isIntersecting
					? productBar.classList.remove("is-visible")
					: productBar.classList.add("is-visible");
			},
			{
				threshold: [0, 1],
			}
		);

		observeBottom.observe(footer);
	}

	const updateDropdown = (target) => {
		const dropDown = target.closest(".js-item-selector-variant");
		const dropDownOptions = dropDown.querySelectorAll(".js-variant-option");

		dropDownOptions.forEach((option) => {
			option.classList.remove("is-active");
		});
		target.classList.add("is-active");
		dropDown.querySelector(".selector-display").innerHTML =
			target.dataset.swatchValue;
		dropDown.classList.remove("is-active");
	};
	let variantSelected = [];

	//native select trigger function on mobile
	if (memberProduct.querySelector("select")) {
		memberProduct.querySelectorAll("select").forEach((select) => {
			select.addEventListener("change", (e) => {
				const sizeOptions = memberProduct.querySelectorAll(
					".js-variant-option.js-select"
				);
				let selectedOption = e.target.options[e.target.selectedIndex];

				//trigger custom selector

				sizeOptions.forEach((sizeOption) => {
					if (sizeOption.dataset.swatchId == selectedOption.dataset.swatchId) {
						sizeOption.click();
					}
				});
			});
		});
	}

	//loop through all variant selections
	memberProduct.querySelectorAll(".js-variant-option").forEach((item) => {
		//retrieve initial swatch value
		if (
			item.classList.contains("is-active") &&
			item.hasAttribute("data-swatch-size")
		) {
			const swatchSize = item.dataset.swatchValue;
			variantSelected[0] = swatchSize;
			item.click();
		}

		if (
			item.classList.contains("is-active") &&
			item.hasAttribute("data-swatch-color")
		) {
			const swatchColor = item.dataset.swatchValue;
			variantSelected[1] = swatchColor;
		}

		if (
			item.classList.contains("is-active") &&
			item.hasAttribute("data-swatch-general")
		) {
			const swatchGeneral = item.dataset.swatchValue;
			variantSelected[2] = swatchGeneral;
		}
	});

	on("body", "click", ".js-variant-option", (e) => {
		const target = e.target;
		const targetType = target.dataset.variantType;

		if (targetType === "size") {
			variantSelected[0] = target.dataset.swatchValue;
			updateDropdown(target);
		} else if (targetType === "color") {
			variantSelected[1] = target.dataset.swatchValue;
			updateDropdown(target);
		} else if (targetType === "general") {
			variantSelected[2] = target.dataset.swatchValue;
			updateDropdown(target);
		}
		let variantSelectedNew = variantSelected.filter((a) => a);

		let variantSelectedTitle = variantSelectedNew.join(" / "); // e.g. xs / black

		//  find corresponding variant title
		const variantSelectedOption = [
			...memberProduct.querySelectorAll(".variant-value"),
		].find((element) => element.dataset.valueTitle === variantSelectedTitle);

		let variantId = variantSelectedOption.value;
		const productSubmit = memberProduct.querySelector("[data-add-id]");
		const productAddOn = memberProduct.querySelector(
			"[data-add-id-membership].is-main-product"
		);

		//update variant id
		if (productAddOn && variantSelectedOption) {
			productAddOn.setAttribute("data-add-id-membership", variantId);
		} else {
			productSubmit.setAttribute("data-add-id", variantId);
		}
	});

	// membership all benefits
	const allBenefits = document.querySelector(".membership-all-benefits");
	if (allBenefits) {
		const benefitBlocks = allBenefits.querySelectorAll(
			".js-membership-all-benefits-block"
		);

		// move benefit image on mousemove
		benefitBlocks.forEach((block) => {
			const image = block.querySelector(
				".js-membership-benefit-mousemove-image"
			);
			if (image) {
				const tl = gsap.timeline({
					ease: "Expo.easeOut",
					defaults: { duration: 0, delay: 0, ease: "Expo.easeOut" },
				});

				block.addEventListener("mousemove", (e) => {
					tl.to(image, {
						// delay: 0.2,
						// duration: 0.01,
						x: e.clientX + 20,
						y: e.clientY + 20,
					});
				});

				block.addEventListener("mouseenter", (e) => {
					tl.to(image, {
						delay: 0.2,
						autoAlpha: 1,
					});
				});

				block.addEventListener("mouseleave", (e) => {
					tl.to(image, {
						autoAlpha: 0,
					});
				});
			}
		});

		// filter list
		if (!allBenefits.querySelector(".js-membership-benefit-filter-btn"))
			return false;
		// map each service block ID to an array of its tags
		const benefitTagMap = {};
		const allFilterBtn = allBenefits.querySelector(
			'.js-membership-benefit-filter-btn[data-tag="all"]'
		);
		let currentFilterBtn = allFilterBtn;

		benefitBlocks.forEach((block, index) => {
			const tags = Array.from(
				block.querySelectorAll(".js-membership-benefit-tag")
			).map((tag) => tag.getAttribute("data-tag"));
			benefitTagMap[index] = tags;
		});

		// when treatment filter button is clicked, only show services with the matching tag
		on("body", "click", ".js-membership-benefit-filter-btn", (e) => {
			const target = e.target.closest(".js-membership-benefit-filter-btn");
			const filter = target.dataset.tag;

			currentFilterBtn.classList.remove("is-active");
			target.classList.add("is-active");
			currentFilterBtn = target;

			benefitBlocks.forEach((block, index) => {
				const isAll = filter === "all";
				const shouldShow = isAll || benefitTagMap[index].includes(filter);

				block.classList.toggle("is-hidden", !shouldShow);
			});
		});
	}
};

const initMembershipPurchase = () => {
	on("body", "click", ".js-membership-purchase-trigger", () => {
		const purchaseForm = document.querySelector(
			".js-membership-purchase-tooltip"
		);

		if (purchaseForm) {
			purchaseForm.querySelector('button[type="submit"]').click();
		}
	});
};

const initAccount = () => {
	// account gate
	const gateWrapper = document.querySelector(".account-gate-wrapper");
	const gateContent = document.querySelector(".account-gate");
	const resetPasswordTrigger = document.querySelector(
		".reset-password-trigger"
	);
	const resetPasswordCancel = document.querySelector(".reset-password-cancel");

	if (gateWrapper) {
		resetPasswordTrigger.addEventListener("click", (event) => {
			event.preventDefault();

			// update URL with hash
			history.pushState(null, null, "#recover");

			// assign active state
			root.classList.add("reset-password-active");

			// update wrapper height to fit account reset form
			gateWrapper.style.height =
				document.querySelector(".account-reset").offsetHeight;

			// focus on email field
			document.querySelector("#account-recover-email").focus();
		});

		if (resetPasswordCancel) {
			resetPasswordCancel.addEventListener("click", (event) => {
				event.preventDefault();

				// remove has from URL
				history.pushState(null, null, " ");

				// remove active state from root
				root.classList.remove("reset-password-active");

				// update wrapper height to fit account reset form
				gateWrapper.style.height = gateContent.offsetHeight;
			});
		}

		// trigger password recovery form if url contains the hash
		if (location.hash == "#recover") {
			resetPasswordTrigger.click();
		}

		// set initial height
		gateWrapper.style.height = gateContent.offsetHeight;
	}
};

// global actions
on("body", "click", ".js-global-overlay", (e) => {
	root.classList.remove("overlay-is-active");
});

document.addEventListener("keyup", (e) => {
	if (e.key === "Escape") root.classList.remove("overlay-is-active");
});

const initHeader = () => {
	const header = document.querySelector(".global-header");
	if (!header) return;

	const headerLogo = header.querySelector(".header-logo");
	let headerLogoWidthMenu = headerLogo ? headerLogo.offsetWidth : 0;
	const headerUnderline = header.querySelector(".header-menu-link-underline");

	const menu = document.querySelectorAll("[data-header-menu]");
	const menuTrigger = document.querySelectorAll("[data-header-menu-trigger]");

	let menuOpenTrigger = null;
	let menuCloseTrigger = null;
	let menuCloseMouseLeaveTrigger = null;
	let menuLocked = false;

	// Slightly longer than --transition-pnl (0.4s) so the fallback only fires
	// when transitionend never arrives.
	const PANEL_TRANSITION_FALLBACK_MS = 450;

	// Focus the first `selector` match inside `panel` once the panel's transform
	// transition has finished, and never let focus() scroll. While the secondary
	// panel is still translated off to the right, a plain focus() makes the
	// browser scroll .mobile-menu-primary sideways to reveal the button (an
	// overflow: hidden box is still programmatically scrollable), and that
	// offset outlives the transition — the panel then looks blank.
	const focusWhenSettled = (panel, selector) => {
		const focusTarget = panel.querySelector(selector);
		if (!focusTarget) return;

		let settled = false;
		let fallbackTimer = null;

		const finish = () => {
			if (settled) return;
			settled = true;
			clearTimeout(fallbackTimer);
			panel.removeEventListener("transitionend", onTransitionEnd);

			// The panel may have been closed again before it finished opening.
			const primaryLink = panel.closest(".primary-link");
			if (primaryLink && !primaryLink.classList.contains("is-active")) return;

			focusTarget.focus({ preventScroll: true });
		};

		const onTransitionEnd = (event) => {
			if (event.target === panel && event.propertyName === "transform") {
				finish();
			}
		};

		panel.addEventListener("transitionend", onTransitionEnd);
		fallbackTimer = setTimeout(finish, PANEL_TRANSITION_FALLBACK_MS);
	};

	// 1. Close Function
	const menuClose = () => {
		root.classList.remove("header-menu-active", "overlay-is-active");

		menuTrigger.forEach((item) => {
			const li = item.closest("li");
			if (li) li.classList.remove("is-active");

			if (window.innerWidth >= 900 && headerUnderline) {
				headerUnderline.style.left = `0`;
				headerUnderline.style.bottom = "0px";
				headerUnderline.style.width = `calc(100% - 2px)`;
			}

			item.setAttribute("aria-expanded", "false");
		});

		// Close desktop mega menus
		menu.forEach((item) => {
			item.classList.remove("is-active");
			item.setAttribute("aria-hidden", "true");
		});

		// Close mobile secondary menus
		document.querySelectorAll(".js-mobile-menu-secondary").forEach((item) => {
			item.setAttribute("aria-hidden", "true");
		});
	};

	// 2. Open Function & Listeners
	menuTrigger.forEach((el) => {
		const menuOpen = (e) => {
			clearTimeout(menuOpenTrigger);

			let target = e.currentTarget;

			let announcementHeight = document.querySelector(".global-announcement")
				? document.querySelector(".global-announcement").offsetHeight
				: 0;

			let liElement = target.closest("li");
			let length = liElement.clientWidth;
			let y = liElement.getBoundingClientRect().top - announcementHeight;
			let x = liElement.getBoundingClientRect().left;

			let offsetY = y > 19 ? 15 : 10;

			function linkCoordinates() {
				if (window.innerWidth >= 900) {
					y = liElement.getBoundingClientRect().top - announcementHeight;
					x = liElement.getBoundingClientRect().left;
					headerLogoWidthMenu = headerLogo ? headerLogo.offsetWidth : 0;
				}
			}

			linkCoordinates();
			window.addEventListener("scroll", linkCoordinates, { passive: true });

			menuOpenTrigger = setTimeout(() => {
				menuClose();

				let menuActive;
				let secondaryMenuContainer;

				if (window.innerWidth <= 900) {
					secondaryMenuContainer = target
						.closest(".primary-link")
						.querySelector(".js-mobile-menu-secondary");
					// Secondary content is rendered directly in the container (no cloned child)
					menuActive = secondaryMenuContainer ?? null;
				} else {
					menuActive = document.querySelector(
						`[data-header-menu="${target.dataset.headerMenuTrigger}"]`
					);
				}

				setTimeout(() => {
					root.classList.add("header-menu-active", "overlay-is-active");
					liElement.classList.add("is-active");
					target.setAttribute("aria-expanded", "true");

					if (window.innerWidth >= 900 && headerUnderline) {
						headerUnderline.style.left = `calc(-${headerLogoWidthMenu}px + ${x}px - 20px)`;
						headerUnderline.style.bottom = `calc(-2px + ${y}px - ${offsetY}px)`;
						headerUnderline.style.width = `${length}px`;
					}

					if (menuActive) {
						menuActive.setAttribute("aria-hidden", "false");

						if (window.innerWidth <= 900) {
							// Mobile: CSS handles slide-in via .primary-link.is-active
							// menuActive IS secondaryMenuContainer — focus its back button
							focusWhenSettled(menuActive, ".mobile-menu-secondary__close");
						} else {
							// Desktop: is-active class drives the mega menu CSS animation
							menuActive.classList.add("is-active");
						}
					}
				}, 1);
			}, 100);
		};

		if (window.innerWidth <= 900) {
			el.addEventListener("click", (e) => {
				if (e.currentTarget.tagName === "A") e.preventDefault();
				if (menuLocked) return;
				menuLocked = true;
				setTimeout(() => {
					menuLocked = false;
				}, 500);

				e.currentTarget.closest(".primary-link").classList.contains("is-active")
					? menuClose()
					: menuOpen(e);
			});
		} else {
			el.addEventListener("click", menuOpen);
			el.addEventListener("mouseenter", (e) => {
				clearTimeout(menuCloseTrigger);
				clearTimeout(menuCloseMouseLeaveTrigger);
				menuOpen(e);
			});
			el.addEventListener("mouseleave", () => {
				clearTimeout(menuOpenTrigger);
				menuCloseMouseLeaveTrigger = setTimeout(menuClose, 400);
			});
		}
	});

	menu.forEach((el) => {
		el.addEventListener("mouseenter", () => {
			clearTimeout(menuCloseTrigger);
			clearTimeout(menuCloseMouseLeaveTrigger);
		});
		el.addEventListener("mouseleave", menuClose);
	});

	// 3. Header Shrink
	function headerShrink() {
		window.innerWidth <= 900
			? root.classList.add("header-is-shrink")
			: root.classList.remove("header-is-shrink");
	}
	headerShrink();
	window.addEventListener("resize", headerShrink, { passive: true });

	// 4. Child Links Mobile Toggle
	const childLinks = document.querySelectorAll(".child-links");

	const childLinkClose = () => {
		childLinks.forEach((target) => target.classList.remove("is-active"));
	};

	const childLinkOpen = (e) => {
		childLinks.forEach((childLink) => childLink.classList.remove("is-active"));
		e.target.closest(".child-links").classList.add("is-active");
	};

	if (typeof on === "function") {
		on("body", "click", "[data-child-link-trigger]", (e) => {
			if (e.target.closest(".child-links").classList.contains("is-active")) {
				childLinkClose();
			} else {
				childLinkOpen(e);
			}
		});

		// Secondary Menu Back Button Close
		on("body", "click", ".mobile-menu-secondary__close", (e) => {
			menuClose();
			const triggerTitle = e.currentTarget.dataset.headerMenuTrigger;
			// Scoped to the mobile menu: the desktop nav has a button with the same
			// data-header-menu-trigger earlier in the DOM. preventScroll for the same
			// reason as focusWhenSettled — the primary list is sliding back in.
			const originalTrigger = document.querySelector(
				`.js-mobile-menu .primary-link-field button[data-header-menu-trigger="${triggerTitle}"]`
			);
			if (originalTrigger) originalTrigger.focus({ preventScroll: true });
		});
	}

	// 5. Reset any open secondary panel once the mobile menu dialog has closed,
	// so the next open starts at the top level.
	document
		.querySelector(".js-mobile-menu")
		?.addEventListener("close", menuClose);

	// 6. Escape closes the desktop mega menu. The mobile menu is a dialog, so
	// the browser handles its Escape.
	document.addEventListener("keydown", (e) => {
		if (e.key === "Escape" && root.classList.contains("header-menu-active")) {
			menuClose();
		}
	});

	// 7. Play the transparent logo video, falling back to the static SVG.
	// The video plays over an SVG wordmark that critical CSS hides on the
	// frontpage; we reveal the SVG (and hide the video) whenever the animation
	// can't play. Covers:
	//   - missing / undecodable video (source 404 or unsupported codec, e.g. a
	//     browser with no alpha-video support) -> play() rejects or 'error' fires
	//   - autoplay blocked (iOS Low Power Mode, data-saver) -> play() rejects
	//   - reduced-motion preference -> we don't play at all
	// Applies to every .header-logo on the page (normal header + lockdown header).
	//
	// The <video> has no `autoplay` attribute; we start it from JS after a short
	// hold so the logo animates in with the same lead-in the old Lottie had.
	// Adjust LOGO_VIDEO_PLAY_DELAY_MS to change that hold.
	const LOGO_VIDEO_PLAY_DELAY_MS = 800;
	const LOGO_VIDEO_WATCHDOG_MS = 2500;
	const prefersReducedMotion = window.matchMedia(
		"(prefers-reduced-motion: reduce)"
	).matches;
	const headerLogos = document.querySelectorAll(".header-logo");
	headerLogos.forEach((logo) => {
		const logoVideo = logo.querySelector(".header-logo-video");
		const logoSvg = logo.querySelector(".svg-logo");
		if (!logoVideo || !logoSvg) return;

		let resolved = false;

		const showSvg = () => {
			if (resolved) return;
			resolved = true;
			logoVideo.style.opacity = 0;
			logoSvg.style.opacity = 1;
		};

		// showVideo is not guarded by `resolved` so a late start can still recover
		// the animation after the watchdog has shown the SVG.
		const showVideo = () => {
			resolved = true;
			logoVideo.style.opacity = 1;
			logoSvg.style.opacity = 0;
		};

		if (prefersReducedMotion) {
			logoVideo.pause();
			showSvg();
			return;
		}

		// Positive signal: the video actually began painting frames.
		logoVideo.addEventListener("playing", showVideo);
		// Fast failure signal (a selected source failed to decode).
		logoVideo.addEventListener("error", showSvg);

		// Hold on the first frame for LOGO_VIDEO_PLAY_DELAY_MS, then start playback.
		setTimeout(() => {
			// play() rejects when autoplay is blocked (iOS Low Power Mode) or no
			// source is playable.
			const playAttempt = logoVideo.play();
			if (playAttempt && typeof playAttempt.then === "function") {
				playAttempt.then(showVideo).catch(showSvg);
			}

			// Watchdog: the reliable catch-all. When every <source> 404s or a
			// browser can't decode any of them, the `error` event and play()
			// rejection are inconsistent across browsers, so if playback hasn't
			// started by the deadline we reveal the SVG. A late `playing` event
			// still swaps the video back in.
			setTimeout(() => {
				if (!resolved) showSvg();
			}, LOGO_VIDEO_WATCHDOG_MS);
		}, LOGO_VIDEO_PLAY_DELAY_MS);
	});
};

const initScrollPosition = () => {
	let ticking = false;

	const handleScroll = () => {
		if (!ticking) {
			window.requestAnimationFrame(() => {
				if (window.scrollY > 0) {
					root.classList.add("is-scrolled");
				} else {
					root.classList.remove("is-scrolled");
				}
				ticking = false;
			});
			ticking = true;
		}
	};
	window.addEventListener("scroll", handleScroll, { passive: true });
	document.addEventListener("DOMContentLoaded", () => {
		if (window.scrollY !== 0) {
			root.classList.add("is-scrolled");
		}
	});

	const setScrolledHero = (entries) => {
		entries[0].isIntersecting
			? root.classList.remove("is-scrolled-hero")
			: root.classList.add("is-scrolled-hero");
	};

	// Kept so a colour swap (productSwap in assets/js-shopify.js), which replaces
	// the hero, can move the observer onto the new node.
	let observeHero = null;

	const observeProductHero = () => {
		const hero2026 = document.querySelector(".product-hero-2026");
		const heroNode =
			hero2026 ||
			document.querySelector(
				".product-hero__gallery, #template-frontpage section:nth-of-type(1)"
			);
		if (!heroNode) return;

		// The 2026 hero's gallery is `position: sticky` in a grid area that spans
		// the whole section, so it only clears the top of the screen at the exact
		// moment the section does — swapping which of the two is observed moves
		// nothing. What is left is a full viewport of scrolling between "Tech
		// Specs" coming into view and the sticky CTA appearing.
		//
		// Shrinking the observer root to a strip across the bottom tenth of the
		// viewport is what moves it: the hero stops intersecting that strip as its
		// bottom edge rises past it, which is as Tech Specs arrives, one screen
		// earlier. A tenth rather than the last percent so a hero barely taller
		// than the viewport still intersects at page load and the CTA cannot flash
		// on top of the hero's own buy button.
		//
		// Scoped to the 2026 hero on purpose: the gift card PDP still renders the
		// 2025 hero, and its timing must not move.
		observeHero?.disconnect();
		observeHero = new IntersectionObserver(setScrolledHero, {
			rootMargin: "0px",
			threshold: hero2026 ? [0.4, 1] : [0.1, 1],
		});
		observeHero.observe(heroNode);
	};

	observeProductHero();
	document.addEventListener("bandit:product-swapped", observeProductHero);

	let observeBottom = new IntersectionObserver(
		(e) => {
			e[0].isIntersecting
				? root.classList.add("is-scrolled-footer")
				: root.classList.remove("is-scrolled-footer");
		},
		{
			threshold: [0],
		}
	);
	observeBottom.observe(document.querySelector(".global-footer"));
};

gScrollSpy.init();
initMembershipPurchase();
// execute pieces and components functions
initStickyBar();
initBadgeTooltips();
initModelViewer();
initAccordions();
initFields();
initMatch();

// execute global and component functions
MouseFollower.initAll();
initPageTransition();
initPageAnimation();
initHeader();
initLightbox();
initContentLayout();
initScrollDistance();
initProductBis();
initQuickAddPanel();
initCardColorSwap();
initDeferredCardImages();

// execute page specific functions
switch (root.id) {
	case "template-frontpage":
		initFrontpage();
		initScrollPosition();
		break;

	case "template-product-single":
		initScrollPosition();
		break;

	case "template-blog-index":
		initBlogIndex();
		break;

	case "template-account":
	case "template-page-orders-tracking":
		initAccount();
		break;

	case "template-page-membership":
	case "template-page-membership-dashboard":
	case "template-page-membership-2024":
		initAccount();
		initMembershipPage();
		break;

	case "template-404":
		initTextFlow();
		break;

	default:
		if (/template-page.*program/.test(root.id)) {
			initOldTvEffect();
			initPhotoDistortion();
		}

		initToggle();
		initImageAccordions();
		break;
}

document.addEventListener("DOMContentLoaded", () => {
	const initAria = () => ariaFocusManager.init();
	if (typeof window.requestIdleCallback === "function") {
		window.requestIdleCallback(initAria, { timeout: 2000 });
	} else {
		setTimeout(initAria, 200);
	}

	// If a real (non-gift) membership is already in cart on load, ensure any
	// members-only locked blocks are swapped for the actual ATC button.
	if (root.classList.contains("cart-has-membership")) {
		updateProductFormsCards();
	}
});

let allPlayersObj = {};
// youtube fix - lace this event at global level
window.onYouTubeIframeAPIReady = () => {
	document
		.querySelectorAll('.js-external-video[data-video-host="youtube"]')
		.forEach((video) => {
			const externalVideoId = video.dataset.videoId;
			const player = new YT.Player(video, {
				videoId: externalVideoId,
				playerVars: { playsinline: 1 },
			});
			allPlayersObj[externalVideoId] = player;
		});
};

window.addEventListener("load", () => {
	document
		.querySelectorAll('.js-external-video[data-video-host="vimeo"]')
		.forEach((video) => {
			const externalVideoId = video.dataset.videoId;
			const player = new Vimeo.Player(video);
			player.on("loaded", () => (allPlayersObj[externalVideoId] = player));
		});

	setTimeout(() => {
		// set a slight delay for player to load completely
		initVimeoPlayer();
		initVideo();
	}, 400);
});

// fitvid
reframe(
	'.fitvid iframe, .js-reframe iframe[src*="vimeo.com"], .js-reframe iframe[src*="youtube.com"]',
	"video-reframe"
);
